import {
  type AuthorityRef,
  type AutoContinuePolicy,
  type CommandReceipt,
  type SessionCreateInput,
  type SessionDetail,
  type ProviderId,
  type SessionAttention,
  type SessionListQuery,
  type SessionListResult,
  type SessionRecoveryInput,
  type SessionManagementMode,
  type SessionStatus,
  type SessionSummary,
  type SessionWorkspace,
  type SessionVisibility,
  type TerminalHistoryQuery,
  type TerminalHistoryResult,
} from "./domain/session.ts";
import { CloudHttpClient } from "./cloud-http.ts";
import { projectDeviceTerminalRelay, type DeviceTerminalRelayEndpoint } from "./cloud-devices-client.ts";
import type { CloudClientMetadata } from "./cloud-http.ts";
import type { Fetcher } from "./local-transport.ts";
import {
  RemoteTerminalObserver,
  type RemoteTerminalObserverEvent,
  type RemoteTerminalSnapshot,
} from "./remote-terminal-observer.ts";
import { RemoteTerminalController, type RemoteTerminalControllerEvent } from "./remote-terminal-controller.ts";
import { RemoteTerminalTicketClient, type NativeRelayClientDescriptor } from "./remote-terminal-ticket-client.ts";
import { CloudflareNativeTerminalMultiplexRelay } from "./cloudflare-native-terminal-multiplex-relay.ts";
import { OrniTransportError, type SessionTransport } from "./transport.ts";
import { randomUuid } from "./ids.ts";
import { decodeSyncStatus, type SyncStatus } from "./sync-status.ts";
import {
  decodeAgentIdentity,
  decodeProviderId,
  requireLegacyProviderId,
  supportedLegacyProviderIds,
} from "./agent-protocol.ts";
import { decodeProviderInventory, type ProviderInventory, type ProviderSettingsInput } from "./provider-inventory.ts";
import { agentOperationName, type AgentOperationInput, type AgentOperationKind, type AgentOperationPollOptions, type AgentOperationReceipt } from "./agent-installations.ts";
import {
  decodeProviderHistoryCatalog,
  decodeProviderHistoryConversationPage,
  decodeProviderHistoryImportResult,
  decodeProviderHistoryScan,
  decodeProviderHistoryScanOperation,
  filterProviderHistoryCatalog,
  providerHistoryTarget,
  type ProviderHistoryCatalog,
  type ProviderHistoryConversationPage,
  type ProviderHistoryImportInput,
  type ProviderHistoryImportResult,
  type ProviderHistoryReadInput,
  type ProviderHistoryScanOperation,
  type ProviderHistoryScanPollOptions,
  type ProviderHistoryScanResult,
  type ProviderHistorySourceInput,
  type ProviderHistoryTarget,
  type ProviderHistoryTargetOptions,
} from "./provider-history.ts";

const DEFAULT_REMOTE_COMMAND_TTL_MS = 60_000;
const LONG_API_MANAGER_REQUEST_TIMEOUT_MS = 90_000;
const DIRECTORY_PICK_REQUEST_TIMEOUT_MS = 5 * 60_000;
const DIRECTORY_LIST_REQUEST_TIMEOUT_MS = 30_000;
// Provider history discovery runs on the target computer and may need to
// open a large SQLite index on its first request. Keep this independent from
// the short default command window used for ordinary UI mutations.
const PROVIDER_HISTORY_REQUEST_TIMEOUT_MS = 90_000;
// Binary image uploads can legitimately take longer than the short control
// plane timeout on a phone/LAN connection. Keep the normal list/send calls
// responsive while giving this bounded 10 MiB body enough time to reach Cloud.
// A complete history catalog is expensive to serialize and relay (Codex
// installations commonly contain thousands of rows). Keep a very short
// transport-local cache so reopening the import sheet does not enqueue a
// second Daemon scan. The Daemon remains the authority; this is only a UI
// acceleration window.
const PROVIDER_HISTORY_CACHE_TTL_MS = 5_000;
// Frame count and output-line count are not equivalent for an interactive
// TUI. The wire snapshot remains capped at 512 KiB, so request enough small
// redraw frames to reconstruct more than the last screen.
const DEFAULT_REMOTE_TERMINAL_HISTORY_LIMIT = 4_096;
const DEVICE_SETTINGS_CACHE_TTL_MS = 30_000;
// Device presence is a shared read used by the Conversations and Agents
// surfaces. Keep it short-lived and single-flight so mounting both routes (or
// React StrictMode effects) cannot enqueue duplicate Cloud reads. Presence is
// never treated as durable authority; an explicit refresh bypasses this cache.
const DEVICE_LIST_CACHE_TTL_MS = 10_000;
const SESSION_PROJECTION_PAGE_SIZE = 100;

export interface RemoteTransportOptions {
  cloudUrl: string;
  /** Explicitly opt in to the Cloudflare terminal data plane. */
  nativeTransport?: "legacy" | "cloudflare";
  userId?: string;
  deviceId?: string;
  accessToken?: string | (() => Promise<string | null>);
  /** Refresh the browser session once after a 401 before retrying the request. */
  recoverUnauthorized?: () => Promise<boolean>;
  requestTimeoutMs?: number;
  fetcher?: Fetcher;
  client?: CloudClientMetadata;
  operationId?: () => string;
  sessionId?: () => string;
  /** Send a caller-assigned Session identity only to Daemons that support it. */
  preallocateSessionId?: boolean;
  now?: () => Date;
  commandReceiptTimeoutMs?: number;
  commandReceiptPollIntervalMs?: number;
  /**
   * Whether this transport can relay the Cloud command-receipt SSE endpoint.
   *
   * Direct Cloud clients use the stream for low-latency completion. A local
   * Daemon JSON gateway deliberately does not proxy unbounded SSE bodies, so
   * gateway-backed clients must use the bounded status poll instead.
   */
  commandReceiptStreamEnabled?: boolean;
  projectionWaitTimeoutMs?: number;
  /** Override the socket endpoint when Cloud traffic is bridged by a local Daemon. */
  remoteTerminalUrl?: (
    access: "observe" | "control",
    deviceId: string,
    sessionId: string,
  ) => string;
  /** Authenticate a local WebSocket before forwarding the Cloud ticket. */
  remoteTerminalAuthenticationFrame?: (
    access: "observe" | "control",
    sessionId: string,
  ) => unknown | Promise<unknown>;
}

/** Options used only by the initial remote-terminal acquisition.  Existing
 * callers keep the historical socket-open behavior; Mobile passes a bounded
 * deadline and waits for the first authoritative `joined` snapshot so the
 * projection wait and observer watchdog share one budget. */
export interface RemoteTerminalOpenOptions {
  /** Total acquisition budget, including projection, ticket and join. */
  deadlineMs?: number;
  /** Wait for the observer's first `joined` frame before returning. */
  waitForInitialSnapshot?: boolean;
}

export interface RemoteDeviceSummary {
  deviceId: string;
  displayName: string;
  platform: string;
  architecture: string;
  capabilities: Record<string, unknown>;
  protocolMin: number | null;
  protocolMax: number | null;
  online: boolean;
  lastSeenAt: string | null;
  enrolledAt: string;
  terminalRelay?: DeviceTerminalRelayEndpoint;
}

export interface RemoteDeviceSettingsUpdate {
  sessionRoot?: string;
  remoteControlRoot?: string;
  defaultPermissionMode?: "read-only" | "workspace-write" | "full-access";
  computerName?: string;
  /** One-way migration marker for the retired Desktop onboarding wizard. */
  completeLegacyOnboarding?: boolean;
  expectedRevision?: number;
}

export interface ApiManagerRequest {
  method: "GET" | "POST" | "DELETE";
  path: string;
  body?: unknown;
}

export interface RemoteDirectoryEntry {
  name: string;
  path: string;
  kind: "directory";
}

export interface RemoteDirectoryListing {
  currentPath: string;
  parentPath: string | null;
  homePath: string;
  directories: RemoteDirectoryEntry[];
  truncated: boolean;
}

/** Device-owned Provider settings accepted by the typed remote command path. */
/** @deprecated Use the transport-neutral ProviderSettingsInput. */
export type RemoteProviderSettingsInput = ProviderSettingsInput;

interface CloudProjection {
  deviceId: string;
  deviceName: string;
  sessionId: string;
  revision: number;
  provider: ProviderId;
  agentId?: string;
  cliVariantId?: string;
  managementMode: SessionManagementMode;
  title: string;
  model: string;
  status: SessionStatus;
  providerReadiness?: "unknown" | "probing" | "ready" | "degraded" | "unavailable";
  attention: SessionAttention;
  visibility: SessionVisibility;
  workspaceId: string;
  workspaceLabel: string;
  branch: string | null;
  processAttemptId: string | null;
  generation: number;
  preview: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  lastActivityAt: string;
  projectedAt: string;
}

interface ProjectionFingerprint {
  revision: number;
  status: SessionStatus;
  providerReadiness?: CloudProjection["providerReadiness"];
  generation: number;
  processAttemptId: string | null;
}

function projectionFingerprint(projection: CloudProjection | null): ProjectionFingerprint | null {
  if (!projection) return null;
  return {
    revision: projection.revision,
    status: projection.status,
    providerReadiness: projection.providerReadiness,
    generation: projection.generation,
    processAttemptId: projection.processAttemptId,
  };
}

function projectionChanged(
  projection: CloudProjection | null,
  baseline: ProjectionFingerprint | null | undefined,
): boolean {
  if (!projection) return false;
  if (!baseline) return true;
  return projection.revision > baseline.revision
    || projection.status !== baseline.status
    || projection.providerReadiness !== baseline.providerReadiness
    || projection.generation !== baseline.generation
    || projection.processAttemptId !== baseline.processAttemptId;
}

interface EnqueuedCommand {
  command: {
    commandId: string;
    createdAt: string;
    status: string;
  };
  result?: unknown;
}

/** Full command receipt fetched after a bounded realtime receipt event. */
export interface RemoteCommandReceiptStatus {
  commandId: string;
  deviceId: string;
  status: string;
  outcome: string | null;
  result: unknown | null;
  createdAt: string;
  updatedAt: string;
  receiptId: string | null;
  receiptCreatedAt: string | null;
  error: RemoteReceiptError | null;
}

interface RemoteReceiptError {
  code: string;
  message: string;
  retryable: boolean;
  [key: string]: unknown;
}

/** A recovery incident returned by an enrolled computer through Cloud. */
export interface RemoteRecoveryIncident {
  incidentId: string;
  deviceId: string;
  deviceName?: string;
  sessionId: string;
  processAttemptId: string;
  previousGeneration: number;
  sessionTitle: string;
  providerId: string;
  workspaceId: string;
  managementMode: "native" | "orni_work";
  restartCause: "unexpected_restart" | "planned_restart" | "upgrade";
  classification: "interrupted_work" | "idle" | "uncertain";
  confidence: "high" | "medium" | "low";
  evidence: string[];
  lastActivityAt: string | null;
  detectedAt: string;
  status: "open" | "continue_requested" | "continue_dispatching" | "continued" | "restored" | "dismissed" | "superseded";
  resolvedAt: string | null;
  detail: string | null;
}

const emptyAutoContinue: AutoContinuePolicy = {
  enabled: false,
  recoveryEnabled: false,
  retryWindowSeconds: 0,
  retryForever: false,
  retryIntervalSeconds: 0,
  attemptsUsed: 0,
  recoveryStartedAt: null,
  lastRecoveryError: null,
  recoveryHistory: [],
};

export class RemoteTransport implements SessionTransport {
  /** Fixed-device Cloudflare streams can be opened before a PTY attempt exists. */
  get supportsPendingTerminalAttach(): boolean { return this.nativeTransport === "cloudflare"; }
  readonly authority: AuthorityRef;
  private readonly options: RemoteTransportOptions;
  private readonly fixedDeviceId?: string;
  private readonly http: CloudHttpClient;
  private readonly cloudUrl: string;
  private readonly nativeTransport: "legacy" | "cloudflare";
  private readonly accessToken?: string | (() => Promise<string | null>);
  private readonly operationId: () => string;
  private readonly sessionId: () => string;
  private readonly preallocateSessionId: boolean;
  private readonly now: () => Date;
  private readonly commandReceiptTimeoutMs: number;
  private readonly commandReceiptPollIntervalMs: number;
  private readonly commandReceiptStreamEnabled: boolean;
  private readonly projectionWaitTimeoutMs: number;
  private readonly remoteTerminalTickets: RemoteTerminalTicketClient;
  private readonly remoteTerminalUrl?: RemoteTransportOptions["remoteTerminalUrl"];
  private readonly remoteTerminalAuthenticationFrame?: RemoteTransportOptions["remoteTerminalAuthenticationFrame"];
  private selectedDeviceId?: string;
  private readonly sessionDevices = new Map<string, string>();
  private readonly sessionProjections = new Map<string, CloudProjection>();
  /** Baselines captured immediately before lifecycle commands are enqueued. */
  private readonly lifecycleProjectionBaselines = new Map<string, ProjectionFingerprint | null>();
  private readonly recoveryDevices = new Map<string, string>();
  private readonly deviceNames = new Map<string, string>();
  private readonly deviceCapabilities = new Map<string, Record<string, unknown>>();
  private readonly deviceSettingsCache = new Map<string, { value: unknown; expiresAt: number }>();
  private deviceListCache: { value: RemoteDeviceSummary[]; expiresAt: number } | null = null;
  private deviceListTask: Promise<RemoteDeviceSummary[]> | null = null;
  /** Share concurrent inventory reads; each one is a remote command on the target Daemon. */
  private readonly providerInventoryTasks = new Map<string, Promise<ProviderInventory>>();
  private readonly providerHistoryCache = new Map<string, { value: ProviderHistoryCatalog; expiresAt: number }>();
  /** Canonical history homes learned from a successful Provider discovery. */
  private readonly providerHistoryHomes = new Map<string, string>();
  /** Remote fallback scan operations are backed by a Cloud command receipt. */
  private readonly providerHistoryScanDevices = new Map<string, {
    deviceId: string;
    importFilter: ProviderHistoryTargetOptions["importStatus"];
    providers: ProviderId[];
    workspacePath: string;
  }>();
  private readonly authorityTransports = new Map<string, RemoteTransport>();
  /** One Cloudflare client socket per device; Session observers share it. */
  private readonly nativeMultiplexRelays = new Map<string, CloudflareNativeTerminalMultiplexRelay>();
  /** Short single-flight cache for the device-wide source wake-up. */
  private readonly fixedDeviceRelayCache = new Map<string, { value: NativeRelayClientDescriptor; ensuredAt: number }>();
  private readonly fixedDeviceRelayTasks = new Map<string, Promise<NativeRelayClientDescriptor | undefined>>();

  constructor(options: RemoteTransportOptions) {
    this.options = { ...options };
    this.fixedDeviceId = options.deviceId;
    this.selectedDeviceId = options.deviceId;
    this.cloudUrl = options.cloudUrl;
    this.nativeTransport = options.nativeTransport ?? "legacy";
    this.accessToken = options.accessToken;
    this.http = new CloudHttpClient(options);
    this.operationId = options.operationId ?? requiredRandomUuid;
    this.sessionId = options.sessionId ?? (() => `ses_${requiredRandomUuid().replaceAll("-", "")}`);
    this.preallocateSessionId = options.preallocateSessionId ?? false;
    this.now = options.now ?? (() => new Date());
    this.commandReceiptTimeoutMs = Math.max(0, options.commandReceiptTimeoutMs ?? 15_000);
    this.commandReceiptPollIntervalMs = Math.max(25, options.commandReceiptPollIntervalMs ?? 250);
    this.commandReceiptStreamEnabled = options.commandReceiptStreamEnabled ?? true;
    this.projectionWaitTimeoutMs = Math.max(
      0,
      options.projectionWaitTimeoutMs ?? Math.max(5_000, this.commandReceiptTimeoutMs),
    );
    this.remoteTerminalTickets = new RemoteTerminalTicketClient({
      cloudUrl: options.cloudUrl,
      accessToken: options.accessToken,
      recoverUnauthorized: options.recoverUnauthorized,
      requestTimeoutMs: options.requestTimeoutMs,
      fetcher: options.fetcher,
      client: options.client,
      operationId: this.operationId,
    });
    this.remoteTerminalUrl = options.remoteTerminalUrl;
    this.remoteTerminalAuthenticationFrame = options.remoteTerminalAuthenticationFrame;
    this.authority = options.deviceId
      ? { kind: "device", deviceId: options.deviceId }
      : { kind: "cloud_user", userId: options.userId ?? "me" };
  }

  forAuthority(authority: AuthorityRef): SessionTransport {
    if (authority.kind !== "device") return this;
    return this.forDevice(authority.deviceId);
  }

  /** Concrete RemoteTransport view for terminal APIs outside SessionTransport. */
  forDevice(deviceId: string): RemoteTransport {
    if (this.fixedDeviceId === deviceId) return this;
    let scoped = this.authorityTransports.get(deviceId);
    if (!scoped) {
      scoped = new RemoteTransport({ ...this.options, deviceId });
      this.authorityTransports.set(deviceId, scoped);
    }
    return scoped;
  }

  /** Open a short-lived, observe-only terminal relay for a current Session. */
  async openRemoteTerminal(
    sessionId: string,
    onEvent?: (event: RemoteTerminalObserverEvent) => void,
    history?: { afterSequence?: number; beforeSequence?: number; limit?: number },
    openOptions: RemoteTerminalOpenOptions = {},
  ): Promise<RemoteTerminalObserver> {
    const deadlineAt = openOptions.deadlineMs === undefined
      ? null
      : Date.now() + Math.max(1, Math.floor(openOptions.deadlineMs));
    const bounded = <T>(task: Promise<T>, message: string): Promise<T> =>
      withDeadline(task, deadlineAt, message);
    const deviceId = await bounded(
      this.resolveSessionDevice(sessionId),
      "Timed out while resolving the remote Session owner.",
    );
    // Keep the live observer join compatible with Cloud relays from before
    // the bounded history extension. Those relays use deny-unknown-fields on
    // the initial join envelope, so sending history_limit on every attach
    // turns a normal terminal into a policy close during rollout. History is
    // requested only by the explicit one-shot history API below.
    const historyRequested = history !== undefined && (
      history.afterSequence !== undefined
      || history.beforeSequence !== undefined
      || history.limit !== undefined
    );
    const createObserver = async (
      projection: CloudProjection | null,
      ticket: string | undefined,
      remainingMs = 10_000,
      relay?: NativeRelayClientDescriptor,
      pendingSessionAttachment = false,
    ) => new RemoteTerminalObserver({
      cloudUrl: this.cloudUrl,
      deviceId,
      ...(!pendingSessionAttachment && projection?.processAttemptId ? { target: {
        session_id: sessionId,
        process_attempt_id: projection.processAttemptId,
        generation: projection.generation,
      } } : { pendingSessionId: sessionId }),
      ticket,
      // The observer handshake is part of the same acquisition budget.  A
      // remaining budget shorter than the historical ten-second socket
      // timeout must not start a second, longer watchdog.
      connectTimeoutMs: Math.max(25, Math.min(10_000, remainingMs)),
      webSocketUrl: this.remoteTerminalUrl?.("observe", deviceId, sessionId),
      authenticationFrame: relay
        ? undefined
        : await this.remoteTerminalAuthenticationFrame?.("observe", sessionId),
      relay,
      multiplex: relay && this.nativeTransport === "cloudflare"
        ? this.nativeMultiplexRelay(deviceId)
        : undefined,
      ...(historyRequested ? {
        historyAfterSequence: history?.afterSequence,
        historyBeforeSequence: history?.beforeSequence,
        historyLimit: Math.min(
          DEFAULT_REMOTE_TERMINAL_HISTORY_LIMIT,
          Math.max(1, history?.limit ?? DEFAULT_REMOTE_TERMINAL_HISTORY_LIMIT),
        ),
      } : {}),
      onEvent,
    });

    // A fixed device route is the normal Cloudflare data-plane path. The
    // route hostname is the device capability, so attachment does not mint
    // a short-lived Session ticket or perform another Cloud control request.
    // Keep the ticket path as a migration fallback for older Cloud snapshots.
    const fixedRelay = this.nativeTransport === "cloudflare"
      ? await bounded(this.fixedDeviceRelay(deviceId), "Timed out while resolving the device terminal relay.")
      : undefined;
    // The device-scoped protocol resolves the current PTY from the Session ID
    // on the owning Daemon. Do not gate CF attachment on the Cloud process
    // projection: it may be stale, and a Session-level stream handles both
    // already-running and stopped Sessions without issuing a Start command.
    const pendingAttach = fixedRelay !== undefined;
    let projection: CloudProjection | null = null;
    if (!pendingAttach) {
      projection = await bounded(
        this.refreshProjection(sessionId),
        "Timed out while resolving the remote Session projection.",
      );
      if (!projection?.processAttemptId) projection = await this.waitForProjection(sessionId, deadlineAt);
      projection = await this.waitForActiveTerminalProjection(sessionId, projection, deadlineAt);
      if (!projection?.processAttemptId) {
        throw new OrniTransportError("CONFLICT", "The Session has no current process attempt.", true);
      }
      assertRemoteTerminalProjectionActive(projection);
    }
    let lastError: unknown = null;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const targetProjection = projection;
      if (!pendingAttach && !targetProjection?.processAttemptId) {
        throw new OrniTransportError("CONFLICT", "The Session has no current process attempt.", true);
      }
      const issued = pendingAttach || fixedRelay ? null : await bounded(this.remoteTerminalTickets.issue({
        deviceId,
        sessionId,
        processAttemptId: targetProjection!.processAttemptId!,
        generation: targetProjection!.generation,
        ttlSeconds: 120,
        operationId: `remote-terminal-observe:${sessionId}:${targetProjection!.processAttemptId}:${targetProjection!.generation}:${attempt + 1}`,
        relay: this.nativeTransport === "cloudflare",
      }), "Timed out while issuing a remote terminal ticket.");
      if (this.nativeTransport === "cloudflare" && !fixedRelay && !issued?.relay) {
        throw new OrniTransportError(
          "CONTRACT_INVALID",
          "Cloudflare terminal transport was requested but Cloud returned no relay descriptor.",
        );
      }
      const remaining = remainingDeadlineMs(deadlineAt);
      const observer = await bounded(
        createObserver(
          targetProjection,
          issued?.ticket,
          remaining,
          this.nativeTransport === "cloudflare" ? fixedRelay ?? issued?.relay : undefined,
          pendingAttach,
        ),
        "Timed out while preparing the remote terminal observer.",
      );
      try {
        await bounded(observer.connect(), "Timed out while connecting the remote terminal.");
        // Pending fixed-device attachments intentionally return before a
        // snapshot. The Session may be stopped for an arbitrary duration;
        // the stream remains subscribed and the coordinator renders its
        // explicit Start action instead of timing out/reconnecting.
        if (openOptions.waitForInitialSnapshot && !pendingAttach) {
          await bounded(observer.initialSnapshotReady, "Timed out while waiting for the remote terminal snapshot.");
        }
        return observer;
      } catch (error) {
        observer.close();
        lastError = error;
        if (!isRetryableTerminalAttachError(error) || attempt >= 3) throw error;
        const retryDelay = Math.min(2_000, 150 * 2 ** attempt, Math.max(0, remainingDeadlineMs(deadlineAt)));
        if (retryDelay > 0) await delay(retryDelay);
        if (deadlineAt !== null && remainingDeadlineMs(deadlineAt) <= 0) break;
        if (pendingAttach) continue;
        const refreshed = await bounded(this.refreshProjection(sessionId), "Timed out while refreshing the terminal projection.").catch(() => null);
        if (!refreshed?.processAttemptId) {
          // A stopped Session is not a retryable terminal attach. Preserve the
          // relay error here; the UI will show its explicit start action.
          if (refreshed && ["stopped", "exited", "error", "archived"].includes(refreshed.status)) throw error;
          continue;
        }
        // A retry can observe the replacement attempt in `starting`. Re-enter
        // the same active-projection gate instead of treating that healthy
        // transition as a terminal failure.
        projection = await this.waitForActiveTerminalProjection(sessionId, refreshed, deadlineAt);
        if (!projection?.processAttemptId) continue;
        assertRemoteTerminalProjectionActive(projection);
      }
    }
    if (deadlineAt !== null && remainingDeadlineMs(deadlineAt) <= 0) {
      throw new OrniTransportError("TIMEOUT", "Remote terminal acquisition timed out before the initial screen was ready.", true);
    }
    throw lastError ?? new OrniTransportError("UNAVAILABLE", "The remote terminal is unavailable.", true);
  }

  private nativeMultiplexRelay(deviceId: string): CloudflareNativeTerminalMultiplexRelay {
    let relay = this.nativeMultiplexRelays.get(deviceId);
    if (!relay) {
      relay = new CloudflareNativeTerminalMultiplexRelay();
      this.nativeMultiplexRelays.set(deviceId, relay);
    }
    return relay;
  }

  private async fixedDeviceRelay(deviceId: string): Promise<NativeRelayClientDescriptor | undefined> {
    const now = Date.now();
    const cached = this.fixedDeviceRelayCache.get(deviceId);
    if (cached && now - cached.ensuredAt < 10_000) return cached.value;
    const pending = this.fixedDeviceRelayTasks.get(deviceId);
    if (pending) return pending;
    const task = (async () => {
      try {
        const value = await this.remoteTerminalTickets.ensureDeviceRelay({
          deviceId,
          // Source activation is device-wide, not Session-wide. A fresh
          // idempotency key is required after the previous ensure command has
          // completed, otherwise a Daemon restart would receive no new wakeup.
          operationId: this.operationId(),
        });
        this.fixedDeviceRelayCache.set(deviceId, { value, ensuredAt: Date.now() });
        return value;
      } catch (cause) {
        // Older Cloud deployments do not expose the fixed-device endpoint yet;
        // returning undefined preserves the ticket/legacy relay fallback.
        if (cause instanceof OrniTransportError && ["NOT_FOUND", "UNAVAILABLE"].includes(cause.code)) return undefined;
        throw cause;
      }
    })();
    this.fixedDeviceRelayTasks.set(deviceId, task);
    try {
      return await task;
    } finally {
      if (this.fixedDeviceRelayTasks.get(deviceId) === task) this.fixedDeviceRelayTasks.delete(deviceId);
    }
  }

  /** Open a fenced writable control attachment. Observe remains read-only. */
  async openRemoteTerminalControl(
    sessionId: string,
    attachmentId: string,
    onEvent?: (event: RemoteTerminalControllerEvent) => void,
  ): Promise<RemoteTerminalController> {
    const deviceId = await this.resolveSessionDevice(sessionId);
    const createController = async (projection: CloudProjection, ticket: string | undefined, relay?: NativeRelayClientDescriptor) => new RemoteTerminalController({
      cloudUrl: this.cloudUrl,
      deviceId,
      attachmentId,
      target: {
        session_id: sessionId,
        process_attempt_id: projection.processAttemptId!,
        generation: projection.generation,
      },
      ticket,
      webSocketUrl: this.remoteTerminalUrl?.("control", deviceId, sessionId),
      authenticationFrame: relay
        ? undefined
        : await this.remoteTerminalAuthenticationFrame?.("control", sessionId),
      relay,
      multiplex: relay && this.nativeTransport === "cloudflare"
        ? this.nativeMultiplexRelay(deviceId)
        : undefined,
      onEvent,
    });
    let projection = await this.refreshProjection(sessionId);
    if (!projection?.processAttemptId) projection = await this.waitForProjection(sessionId);
    projection = await this.waitForActiveTerminalProjection(sessionId, projection);
    if (!projection?.processAttemptId) {
      throw new OrniTransportError("CONFLICT", "The Session has no current process attempt.", true);
    }
    assertRemoteTerminalProjectionActive(projection);
    // Control attachments race the same Daemon attempt publication and relay
    // handshake as observe attachments. A transient source close can happen
    // even when the attempt identity is unchanged, so retry with a fresh
    // ticket a bounded number of times instead of requiring target rotation.
    let lastError: unknown = null;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const fixedRelay = this.nativeTransport === "cloudflare"
        ? await this.fixedDeviceRelay(deviceId)
        : undefined;
      const issued = fixedRelay ? null : await this.remoteTerminalTickets.issue({
        deviceId,
        sessionId,
        processAttemptId: projection.processAttemptId!,
        generation: projection.generation,
        ttlSeconds: 120,
        operationId: `remote-terminal-control:${attachmentId}:${sessionId}:${projection.processAttemptId}:${projection.generation}:${attempt + 1}`,
        access: "control",
        relay: this.nativeTransport === "cloudflare",
      });
      if (this.nativeTransport === "cloudflare" && !fixedRelay && !issued?.relay) {
        throw new OrniTransportError(
          "CONTRACT_INVALID",
          "Cloudflare terminal transport was requested but Cloud returned no relay descriptor.",
        );
      }
      const controller = await createController(
        projection,
        issued?.ticket,
        this.nativeTransport === "cloudflare" ? fixedRelay ?? issued?.relay : undefined,
      );
      try {
        await controller.connect();
        return controller;
      } catch (error) {
        controller.close();
        lastError = error;
        if (!isRetryableTerminalAttachError(error) || attempt >= 3) throw error;
        await delay(Math.min(2_000, 150 * 2 ** attempt));
        const refreshed = await this.refreshProjection(sessionId).catch(() => null);
        if (!refreshed?.processAttemptId) {
          if (refreshed && ["stopped", "exited", "error", "archived"].includes(refreshed.status)) throw error;
          continue;
        }
        projection = refreshed;
        assertRemoteTerminalProjectionActive(projection);
      }
    }
    throw lastError ?? new OrniTransportError("UNAVAILABLE", "The remote terminal control is unavailable.", true);
  }

  /**
   * Read the current process' bounded terminal scrollback through the same
   * observe relay used by the live terminal. Terminal bytes are intentionally
   * not persisted in Cloud; the Daemon owns this short-lived snapshot.
   */
  async getRemoteTerminalHistory(
    sessionId: string,
    query: TerminalHistoryQuery = {},
  ): Promise<TerminalHistoryResult> {
    let resolveJoined!: (snapshot: RemoteTerminalSnapshot) => void;
    let rejectJoined!: (error: unknown) => void;
    const joined = new Promise<RemoteTerminalSnapshot>((resolve, reject) => {
      resolveJoined = resolve;
      rejectJoined = reject;
    });
    let observer: RemoteTerminalObserver | null = null;
    try {
      observer = await this.openRemoteTerminal(sessionId, (event) => {
        if (event.type === "joined") resolveJoined(event.snapshot);
        else if (event.type === "closed") rejectJoined(
          new OrniTransportError("UNAVAILABLE", event.reason, true),
        );
      }, query);
      const snapshot = await joined;
      return remoteSnapshotHistory(snapshot, sessionId, query);
    } finally {
      observer?.close();
    }
  }

  async listSessions(query: SessionListQuery = {}): Promise<SessionListResult> {
    // Session browsing is fleet-wide. The selected computer is a command
    // target, not a hidden filter that makes other computers disappear.
    const projections = query.limit === undefined
      ? await this.listProjectionPages()
      : await this.listProjectionWindow(
        Math.max(1, Math.floor(query.limit)),
        Math.max(0, Math.floor(query.offset ?? 0)),
      );
    this.rememberProjections(projections);
    // The page may have come from a lagging Cloud replica. Use the same
    // revision-fenced canonical rows as targeted reads so a fleet refresh
    // cannot repaint Mobile with an old attempt immediately before attach.
    const canonical = projections.map((projection) =>
      this.sessionProjections.get(remoteSessionKey(projection.deviceId, projection.sessionId)) ?? projection);
    const sessions = canonical.map((projection) => projectRemote(projection, this.now()));
    const filtered = filterSessions(sessions, query);
    return {
      sessions: sortSessions(filtered, query.sort),
      total: filtered.length,
      revision: Math.max(0, ...canonical.map((projection) => projection.revision)),
    };
  }

  /** Resolve a Session by identity across the fleet, regardless of the
   * currently selected computer used by the list/new-session view. */
  async getSession(sessionId: string): Promise<SessionSummary> {
    // A Session projection is also the source of the current process attempt.
    // Returning the cached creation snapshot here can leave Cloud Web stuck in
    // "starting" forever, so every detail read refreshes the fleet projection.
    const projection = await this.refreshProjection(sessionId);
    if (!projection) {
      throw new OrniTransportError("NOT_FOUND", "The remote Session was not found.");
    }
    return projectRemote(projection, this.now());
  }

  async getSessionWorkspace(sessionId: string): Promise<SessionWorkspace> {
    const projection = await this.refreshProjection(sessionId);
    if (!projection) throw new OrniTransportError("NOT_FOUND", "The remote Session was not found.");
    const response = await this.enqueue(
      projection.deviceId,
      "sessions.workspace",
      sessionId,
      {},
    );
    const result = response.result ?? await this.waitForCommandResult(
      projection.deviceId,
      response.command.commandId,
      "read the Session working directory",
    );
    const row = record(result);
    const path = text(row.path ?? row.workspace_path);
    if (!path.trim()) throw new OrniTransportError("CONTRACT_INVALID", "The target computer returned an empty Session working directory.");
    return { path: path.trim() };
  }

  async listDevices(options: { refresh?: boolean } = {}): Promise<RemoteDeviceSummary[]> {
    const now = Date.now();
    if (!options.refresh && this.deviceListCache && this.deviceListCache.expiresAt > now) {
      return this.deviceListCache.value.slice();
    }
    if (this.deviceListTask) return this.deviceListTask.then((devices) => devices.slice());
    const task = (async () => {
      const value = await this.http.request("/v1/devices");
      if (!Array.isArray(value)) throw new OrniTransportError("CONTRACT_INVALID", "Cloud returned an invalid device list.");
      const devices = value.map((item) => {
        const row = record(item);
        const terminalRelay = projectDeviceTerminalRelay(row.terminalRelay);
        return {
          deviceId: text(row.deviceId),
          displayName: textOr(row.displayName, text(row.deviceId)),
          platform: textOr(row.platform, "unknown"),
          architecture: textOr(row.architecture, "unknown"),
          capabilities: record(row.capabilities ?? {}),
          protocolMin: nullableInteger(row.protocolMin),
          protocolMax: nullableInteger(row.protocolMax),
          online: row.online === true,
          lastSeenAt: nullableString(row.lastSeenAt),
          enrolledAt: text(row.enrolledAt),
          ...(terminalRelay ? { terminalRelay } : {}),
        };
      });
      for (const device of devices) this.deviceNames.set(device.deviceId, device.displayName);
      for (const device of devices) this.deviceCapabilities.set(device.deviceId, device.capabilities);
      this.deviceListCache = { value: devices, expiresAt: Date.now() + DEVICE_LIST_CACHE_TTL_MS };
      return devices;
    })();
    this.deviceListTask = task;
    void task.finally(() => {
      if (this.deviceListTask === task) this.deviceListTask = null;
    }).catch(() => undefined);
    return task.then((devices) => devices.slice());
  }

  /** Select the computer used for new Sessions and commands without rebuilding the transport. */
  setTargetDevice(deviceId: string | null): void {
    const value = deviceId?.trim();
    if (this.fixedDeviceId && value && value !== this.fixedDeviceId) {
      throw new OrniTransportError("INVALID_ARGUMENT", "This Cloud client is pinned to one computer.");
    }
    this.selectedDeviceId = value || undefined;
  }

  getTargetDevice(): string | null {
    return this.selectedDeviceId ?? null;
  }

  /**
   * Recovery incidents are computer-owned state. With no explicit device,
   * query every online enrolled computer so the Cloud UI can show one fleet
   * view; callers may pass a device to scope the read. Offline computers are
   * intentionally skipped because a daemon-target command cannot be applied
   * until that computer reconnects.
   */
  async listRecoveryIncidents(deviceId?: string): Promise<RemoteRecoveryIncident[]> {
    const targetIds = deviceId
      ? [deviceId]
      : this.fixedDeviceId
        ? [this.fixedDeviceId]
        : (await this.listDevices()).filter((device) => device.online).map((device) => device.deviceId);
    const results = await Promise.allSettled(targetIds.map(async (target) => {
      const response = await this.enqueue(target, "recovery.incidents.list", null, {});
      const result = response.result ?? await this.waitForCommandResult(target, response.command.commandId, "read recovery incidents");
      const root = record(result);
      return array(root.incidents).map((item) => mapRemoteRecoveryIncident(item, target, this.deviceNames.get(target)));
    }));
    const incidents: RemoteRecoveryIncident[] = [];
    let firstError: unknown = null;
    for (const result of results) {
      if (result.status === "fulfilled") {
        for (const incident of result.value) {
          this.recoveryDevices.set(incident.incidentId, incident.deviceId);
          incidents.push(incident);
        }
      } else if (!firstError) {
        firstError = result.reason;
      }
    }
    if (!incidents.length && firstError && targetIds.length > 0) throw firstError;
    return incidents.sort((a, b) => Date.parse(b.detectedAt) - Date.parse(a.detectedAt));
  }

  async resolveRecoveryIncident(
    incidentId: string,
    action: "continue" | "restore_only" | "dismiss",
  ): Promise<RemoteRecoveryIncident> {
    let deviceId = this.recoveryDevices.get(incidentId);
    if (!deviceId) {
      await this.listRecoveryIncidents();
      deviceId = this.recoveryDevices.get(incidentId);
    }
    if (!deviceId) {
      throw new OrniTransportError("NOT_FOUND", "The recovery incident was not found on any enrolled computer.");
    }
    const response = await this.enqueue(deviceId, "recovery.incident.resolve", null, {
      incident_id: incidentId,
      action,
    });
    const result = response.result ?? await this.waitForCommandResult(deviceId, response.command.commandId, "resolve recovery incident");
    const incident = mapRemoteRecoveryIncident(result, deviceId, this.deviceNames.get(deviceId));
    this.recoveryDevices.set(incident.incidentId, incident.deviceId);
    return incident;
  }

  /** Read provider installation/configuration metadata from one target computer. */
  async getProviderInventory(deviceId?: string): Promise<ProviderInventory> {
    const resolved = await this.resolveDevice(deviceId);
    const existing = this.providerInventoryTasks.get(resolved);
    if (existing) return existing;
    const task = (async () => {
      const response = await this.enqueue(resolved, "providers.inventory.get", null, {});
      const result = response.result
        ?? await this.waitForCommandResult(resolved, response.command.commandId, "read provider inventory");
      return decodeProviderInventory(result);
    })();
    this.providerInventoryTasks.set(resolved, task);
    // Do not retain failures: a target that comes back online must be able to
    // retry immediately, while concurrent callers still share this attempt.
    void task.finally(() => {
      if (this.providerInventoryTasks.get(resolved) === task) this.providerInventoryTasks.delete(resolved);
    }).catch(() => undefined);
    return task;
  }

  async refreshProviderInventory(deviceId?: string): Promise<ProviderInventory> {
    const resolved = await this.resolveDevice(deviceId);
    for (const key of this.providerHistoryHomes.keys()) {
      if (key.startsWith(`${resolved}|`)) this.providerHistoryHomes.delete(key);
    }
    const response = await this.enqueue(resolved, "providers.inventory.refresh", null, {});
    const result = response.result
      ?? await this.waitForCommandResult(resolved, response.command.commandId, "refresh provider inventory");
    return decodeProviderInventory(result);
  }

  async updateProviderSettings(
    providerId: string,
    input: RemoteProviderSettingsInput,
    deviceId?: string,
  ): Promise<unknown> {
    const normalized = providerId.trim();
    if (!normalized) throw new OrniTransportError("INVALID_ARGUMENT", "Provider id is required.");
    const resolved = await this.resolveDevice(deviceId);
    // A changed Provider home must not be reused by the next history browse.
    this.providerHistoryHomes.delete(providerHistoryHomeKey(resolved, normalized));
    const response = await this.enqueue(resolved, "providers.settings.update", null, {
      provider_id: normalized,
      settings: {
        enabled: input.enabled,
        ...(input.executablePath === undefined ? {} : { executable_path: input.executablePath }),
        ...(input.homeDirectory === undefined ? {} : { home_directory: input.homeDirectory }),
        ...(input.apiBaseUrl === undefined ? {} : { api_base_url: input.apiBaseUrl }),
        ...(input.protocolId === undefined ? {} : { protocol_id: input.protocolId }),
        ...(input.authKind === undefined ? {} : { auth_kind: input.authKind }),
        ...(input.clearCredentialMode === undefined
          ? {}
          : { clear_credential_mode: input.clearCredentialMode }),
        ...(input.apiKey === undefined ? {} : { api_key: input.apiKey }),
        ...(input.clearApiKey === undefined ? {} : { clear_api_key: input.clearApiKey }),
        ...(input.defaultModel === undefined ? {} : { default_model: input.defaultModel }),
        ...(input.expectedRevision === undefined ? {} : { expected_revision: input.expectedRevision }),
      },
    });
    return response.result ?? this.waitForCommandResult(resolved, response.command.commandId, "update provider settings");
  }

  /** Execute a typed Agent lifecycle operation on one enrolled computer. */
  async agentOperation(input: AgentOperationInput, deviceId?: string): Promise<AgentOperationReceipt> {
    const agentId = input.agentId.trim();
    if (!agentId) throw new OrniTransportError("INVALID_ARGUMENT", "Agent id is required.");
    const resolved = await this.resolveDevice(deviceId);
    const operationId = input.operationId?.trim() || this.operationId();
    const operation = agentOperationName(input.operation);
    const response = await this.enqueue(resolved, operation, null, {
      agent_id: agentId,
      operation: input.operation,
      ...(input.expectedRevision === undefined ? {} : { expected_revision: input.expectedRevision }),
    }, DEFAULT_REMOTE_COMMAND_TTL_MS, operationId, false);
    const result = response.result;
    const daemonEnvelope = extractDaemonReceiptEnvelope(result);
    if (daemonEnvelope) {
      return projectEnqueuedAgentOperation(
        response,
        result,
        input.operation,
        operationId,
        resolved,
      );
    }
    if (result && typeof result === "object" && !Array.isArray(result)) {
      return {
        id: response.command.commandId,
        command: operation,
        status: "succeeded",
        requestedAt: response.command.createdAt,
        completedAt: new Date().toISOString(),
        detail: "The target computer applied the Agent lifecycle operation.",
        operationId,
        commandId: response.command.commandId,
        receiptId: null,
        operation: input.operation,
        targetDeviceId: resolved,
        phase: "completed",
        entityRevision: extractEntityRevision(result),
        retryable: false,
        errorCode: null,
        completionKnown: true,
        observation: "complete",
      };
    }
    const accepted: AgentOperationReceipt = {
      id: response.command.commandId,
      command: operation,
      status: "accepted",
      requestedAt: response.command.createdAt,
      completedAt: null,
      detail: "Cloud accepted the Agent lifecycle operation; the target computer has not applied it yet.",
      operationId,
      commandId: response.command.commandId,
      receiptId: null,
      operation: input.operation,
      targetDeviceId: resolved,
      phase: "accepted",
      entityRevision: null,
      retryable: false,
      errorCode: null,
      completionKnown: true,
      observation: "active",
    };
    // Package-manager work can take minutes. Return immediately with the
    // durable acceptance receipt; callers can observe completion through the
    // existing receipt status/stream APIs without issuing a duplicate
    // operation after a browser timeout.
    return accepted;
  }

  async getAgentOperationStatus(
    commandId: string,
    operationKind: AgentOperationKind,
    deviceId?: string,
  ): Promise<AgentOperationReceipt> {
    const id = commandId.trim();
    if (!id) throw new OrniTransportError("INVALID_ARGUMENT", "Command id is required.");
    const resolved = await this.resolveDevice(deviceId);
    const status = await this.getCommandReceiptStatus(resolved, id);
    return projectAgentOperationStatus(status, operationKind);
  }

  async waitForAgentOperation(
    commandId: string,
    operationKind: AgentOperationKind,
    options: AgentOperationPollOptions = {},
    deviceId?: string,
  ): Promise<AgentOperationReceipt> {
    const timeoutMs = Math.max(1, options.timeoutMs ?? this.commandReceiptTimeoutMs);
    const intervalMs = Math.max(50, options.intervalMs ?? this.commandReceiptPollIntervalMs);
    const deadline = Date.now() + timeoutMs;
    let latest = await this.getAgentOperationStatus(commandId, operationKind, deviceId);
    while (!isCommandReceiptTerminal(latest.status) && Date.now() < deadline) {
      if (options.signal?.aborted) {
        return {
          ...latest,
          observation: "cancelled",
          detail: "Agent operation polling was cancelled; the target computer continues processing it.",
        };
      }
      await delay(Math.min(intervalMs, Math.max(1, deadline - Date.now())));
      latest = await this.getAgentOperationStatus(commandId, operationKind, deviceId);
    }
    return latest;
  }

  /** Read the redacted API Manager snapshot from one target computer. */
  async getApiManagerSnapshot(deviceId?: string): Promise<unknown> {
    const resolved = await this.resolveDevice(deviceId);
    const response = await this.enqueue(resolved, "api.manager.snapshot", null, {});
    return response.result ?? this.waitForCommandResult(resolved, response.command.commandId, "read API Manager settings");
  }

  async readApiManager(path: string, deviceId?: string): Promise<unknown> {
    const target = deviceId ?? this.selectedDeviceId ?? this.fixedDeviceId;
    const resolved = target ?? (await this.listDevices()).find((device) => device.online)?.deviceId;
    if (!resolved) throw new OrniTransportError("UNAVAILABLE", "No enrolled computer is online.", true);
    const response = await this.enqueue(resolved, "api.manager.read", null, { path });
    return response.result ?? this.waitForCommandResult(resolved, response.command.commandId, "read API Manager data");
  }

  /** Send one allowlisted request to the target computer's Orni API Daemon. */
  async requestApiManager(request: ApiManagerRequest, deviceId?: string): Promise<unknown> {
    if (request.method !== "GET" && request.method !== "POST" && request.method !== "DELETE") {
      throw new OrniTransportError("INVALID_ARGUMENT", "Unsupported API Manager request method.");
    }
    if (!request.path.startsWith("/v1/api-manager") || request.path.includes("..")) {
      throw new OrniTransportError("INVALID_ARGUMENT", "The API Manager request path is invalid.");
    }
    if (request.method !== "POST" && request.body !== undefined) {
      throw new OrniTransportError("INVALID_ARGUMENT", "API Manager GET and DELETE requests cannot include a body.");
    }
    const target = deviceId ?? this.selectedDeviceId ?? this.fixedDeviceId;
    const resolved = target ?? (await this.listDevices()).find((device) => device.online)?.deviceId;
    if (!resolved) throw new OrniTransportError("UNAVAILABLE", "No enrolled computer is online.", true);
    const timeoutMs = longApiManagerRequest(request.path)
      ? Math.max(this.commandReceiptTimeoutMs, LONG_API_MANAGER_REQUEST_TIMEOUT_MS)
      : this.commandReceiptTimeoutMs;
    const response = await this.enqueue(resolved, "api.manager.request", null, {
      method: request.method,
      path: request.path,
      ...(request.body === undefined ? {} : { body: request.body }),
    }, longApiManagerRequest(request.path) ? LONG_API_MANAGER_REQUEST_TIMEOUT_MS : DEFAULT_REMOTE_COMMAND_TTL_MS);
    if ("result" in response) return response.result;
    return this.waitForCommandResult(
      resolved,
      response.command.commandId,
      "apply API Manager request",
      timeoutMs,
    );
  }

  async getDeviceSettings(deviceId?: string): Promise<unknown> {
    const resolved = await this.resolveDevice(deviceId);
    const cached = this.deviceSettingsCache.get(resolved);
    if (cached && cached.expiresAt > Date.now()) return cached.value;
    const response = await this.enqueue(resolved, "settings.get", null, {});
    const value = response.result
      ?? await this.waitForCommandResult(resolved, response.command.commandId, "read device settings");
    this.deviceSettingsCache.set(resolved, {
      value,
      expiresAt: Date.now() + DEVICE_SETTINGS_CACHE_TTL_MS,
    });
    return value;
  }

  async pickDirectoryPath(deviceId: string): Promise<string | null> {
    const normalizedDeviceId = deviceId.trim();
    if (!normalizedDeviceId) {
      throw new OrniTransportError("INVALID_ARGUMENT", "A target computer is required to choose a directory.");
    }
    const resolved = await this.resolveDevice(normalizedDeviceId);
    const response = await this.enqueue(
      resolved,
      "directory.pick",
      null,
      {},
      DIRECTORY_PICK_REQUEST_TIMEOUT_MS,
    );
    const result = response.result ?? await this.waitForCommandResult(
      resolved,
      response.command.commandId,
      "choose a directory",
      DIRECTORY_PICK_REQUEST_TIMEOUT_MS,
    );
    return directoryPickPath(result);
  }

  async listDirectories(deviceId: string, path?: string): Promise<RemoteDirectoryListing> {
    const normalizedDeviceId = deviceId.trim();
    if (!normalizedDeviceId) {
      throw new OrniTransportError("INVALID_ARGUMENT", "A target computer is required to browse directories.");
    }
    const normalizedPath = path?.trim();
    if (path !== undefined && !normalizedPath) {
      throw new OrniTransportError("INVALID_ARGUMENT", "The directory path cannot be empty.");
    }
    const resolved = await this.resolveDevice(normalizedDeviceId);
    const response = await this.enqueue(
      resolved,
      "directory.list",
      null,
      normalizedPath ? { path: normalizedPath } : {},
      DIRECTORY_LIST_REQUEST_TIMEOUT_MS,
    );
    const result = response.result ?? await this.waitForCommandResult(
      resolved,
      response.command.commandId,
      "browse directories",
      DIRECTORY_LIST_REQUEST_TIMEOUT_MS,
    );
    return directoryListing(result);
  }

  async updateDeviceSettings(
    input: string | RemoteDeviceSettingsUpdate,
    expectedRevision?: number,
    deviceId?: string,
  ): Promise<unknown> {
    const update = typeof input === "string"
      ? { sessionRoot: input, expectedRevision }
      : input;
    const resolved = await this.resolveDevice(deviceId);
    this.deviceSettingsCache.delete(resolved);
    const response = await this.enqueue(resolved, "settings.update", null, {
      ...(update.sessionRoot === undefined ? {} : { session_root: update.sessionRoot }),
      ...(update.remoteControlRoot === undefined
        ? {}
        : { remote_control_root: update.remoteControlRoot }),
      ...(update.defaultPermissionMode === undefined
        ? {}
        : { default_permission_mode: update.defaultPermissionMode }),
      ...(update.computerName === undefined ? {} : { computer_name: update.computerName }),
      ...(update.completeLegacyOnboarding === true ? { complete_legacy_onboarding: true } : {}),
      ...(update.expectedRevision === undefined
        ? {}
        : { expected_revision: update.expectedRevision }),
    });
    const value = response.result
      ?? await this.waitForCommandResult(resolved, response.command.commandId, "update device settings");
    this.deviceSettingsCache.set(resolved, {
      value,
      expiresAt: Date.now() + DEVICE_SETTINGS_CACHE_TTL_MS,
    });
    return value;
  }

  async discoverProviderHistory(
    sources: ProviderHistorySourceInput[] = [],
    targetOptions?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryCatalog> {
    const options = providerHistoryTarget(targetOptions);
    const target = options.deviceId ?? this.selectedDeviceId ?? this.fixedDeviceId;
    const resolved = target ?? (await this.listDevices()).find((device) => device.online)?.deviceId;
    if (!resolved) throw new OrniTransportError("UNAVAILABLE", "No enrolled computer is online.", true);
    const cacheKey = providerHistoryCacheKey(resolved, options.provider, resolvedSourcesKey(sources));
    // A bounded page must never be cached as if it were a complete catalog:
    // doing so made the next page request silently reuse the first ten rows.
    // The Daemon owns the complete source-fingerprint cache; this map only
    // accelerates complete, unfiltered catalog reads on the transport.
    const paged = options.cursor !== undefined || options.cursorToken !== undefined || options.limit !== undefined;
    if (options.importStatus === "all" && !options.refresh && !paged) {
      const cached = this.providerHistoryCache.get(cacheKey);
      if (cached && cached.expiresAt > Date.now()) return filterProviderHistoryCatalog(cached.value, options.provider);
      if (cached) this.providerHistoryCache.delete(cacheKey);
    }
    // Resolve an empty-source request to the selected CLI's home reported by
    // the target Daemon. The Daemon remains the only process that reads and
    // indexes Provider files; this explicit source merely prevents an older
    // relay/Daemon from losing the provider selector while constructing its
    // inventory.
    let resolvedSources = sources;
    if (!resolvedSources.length && options.provider && !options.refresh) {
      const cachedHome = this.providerHistoryHomes.get(providerHistoryHomeKey(resolved, options.provider));
      if (cachedHome) resolvedSources = [{ provider: options.provider, homeDirectory: cachedHome }];
    }
    if (!resolvedSources.length && options.provider) {
      try {
        const inventory = await this.getProviderInventory(resolved);
        const provider = inventory.providers.find((item) => item.providerId === options.provider);
        const homeDirectory = provider?.configuredHomeDirectory?.trim() || provider?.homeDirectory.trim();
        if (homeDirectory) {
          resolvedSources = [{ provider: options.provider, homeDirectory }];
        }
      } catch {
        // Preserve the normal Daemon inventory fallback if an older target
        // does not expose providers.inventory.get yet.
      }
    }
    let response;
    try {
      response = await this.enqueue(resolved, "provider_history.discover", null, {
        sources: resolvedSources.map((source) => ({ provider: source.provider, home_directory: source.homeDirectory })),
        import_filter: options.importStatus,
        ...(options.provider ? { provider: options.provider } : {}),
        ...(options.cursor !== undefined ? { cursor: options.cursor } : {}),
        ...(options.cursorToken ? { revision_cursor: options.cursorToken } : {}),
        ...(options.limit !== undefined ? { limit: options.limit } : {}),
      }, PROVIDER_HISTORY_REQUEST_TIMEOUT_MS);
    } catch (cause) {
      // Older enrolled Daemons reject the provider selector. Retry once with
      // the legacy payload, then project the requested provider locally.
      // A legacy Daemon may reject the optional provider selector.  Only
      // retry its legacy payload when we have resolved an explicit source for
      // the selected CLI.  Falling back with an empty `sources` array would
      // ask the Daemon to scan every installed CLI, which is both slow and
      // violates the provider-tab boundary (Codex can contain thousands of
      // conversations).
      // A revision-bound cursor cannot be safely replayed against an older
      // Daemon which does not understand the fence. Surface that contract
      // error instead of silently falling back to an unfenced page.
      if (options.cursorToken || !options.provider || !resolvedSources.length || !isProviderSelectorCompatibilityError(cause)) throw cause;
      response = await this.enqueue(resolved, "provider_history.discover", null, {
        sources: resolvedSources.map((source) => ({ provider: source.provider, home_directory: source.homeDirectory })),
        import_filter: options.importStatus,
        ...(options.cursor !== undefined ? { cursor: options.cursor } : {}),
        ...(options.cursorToken ? { revision_cursor: options.cursorToken } : {}),
        ...(options.limit !== undefined ? { limit: options.limit } : {}),
      }, PROVIDER_HISTORY_REQUEST_TIMEOUT_MS);
    }
    const value = response.result ?? await this.waitForCommandResult(resolved, response.command.commandId, "discover Provider history", PROVIDER_HISTORY_REQUEST_TIMEOUT_MS);
    const catalog = decodeProviderHistoryCatalog(value, { kind: "device", deviceId: resolved }, resolved);
    if (options.provider) {
      const home = catalog.providers.find((item) => item.provider === options.provider)?.homeDirectory?.trim();
      if (home) this.providerHistoryHomes.set(providerHistoryHomeKey(resolved, options.provider), home);
    }
    if (options.importStatus === "all" && !paged && !catalog.providers.some((item) => item.error)) {
      const entry = { value: catalog, expiresAt: Date.now() + PROVIDER_HISTORY_CACHE_TTL_MS };
      this.providerHistoryCache.set(cacheKey, entry);
      // The first request often resolves a provider home through inventory.
      // Also retain that result under the explicit source key so subsequent
      // requests remain cacheable regardless of how the caller supplied it.
      const resolvedKey = providerHistoryCacheKey(resolved, options.provider, resolvedSourcesKey(resolvedSources));
      this.providerHistoryCache.set(resolvedKey, entry);
      if (options.provider) this.providerHistoryCache.set(providerHistoryCacheKey(resolved, options.provider, ""), entry);
    }
    return filterProviderHistoryCatalog(catalog, options.provider);
  }

  async scanProviderHistory(
    workspacePath: string,
    providers: ProviderHistorySourceInput["provider"][] = supportedLegacyProviderIds(),
    targetOptions?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryScanResult> {
    const options = providerHistoryTarget(targetOptions);
    const target = options.deviceId ?? this.selectedDeviceId ?? this.fixedDeviceId;
    const resolved = target ?? (await this.listDevices()).find((device) => device.online)?.deviceId;
    if (!resolved) throw new OrniTransportError("UNAVAILABLE", "No enrolled computer is online.", true);
    const response = await this.enqueue(resolved, "provider_history.scan", null, {
      workspace_path: workspacePath,
      providers,
      import_filter: options.importStatus,
    }, PROVIDER_HISTORY_REQUEST_TIMEOUT_MS);
    const value = response.result ?? await this.waitForCommandResult(resolved, response.command.commandId, "scan Provider history", PROVIDER_HISTORY_REQUEST_TIMEOUT_MS);
    return decodeProviderHistoryScan(value, { kind: "device", deviceId: resolved }, resolved);
  }

  /** Start a Daemon-owned, cancellable history scan. */
  async createProviderHistoryScan(
    workspacePath: string,
    providers: ProviderHistorySourceInput["provider"][] = supportedLegacyProviderIds(),
    targetOptions?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryScanOperation> {
    const path = workspacePath.trim();
    if (!path) throw new TypeError("workspacePath is required");
    const options = providerHistoryTarget(targetOptions);
    const resolved = await this.resolveDevice(options.deviceId);
    const response = await this.enqueue(resolved, "provider_history.scan.create", null, {
      workspace_path: path,
      providers,
      import_filter: options.importStatus,
    }, PROVIDER_HISTORY_REQUEST_TIMEOUT_MS);
    const value = response.result
      ?? await this.waitForCommandResult(
        resolved,
        response.command.commandId,
        "create Provider history scan",
        PROVIDER_HISTORY_REQUEST_TIMEOUT_MS,
      );
    const operation = decodeProviderHistoryScanOperation(value);
    this.providerHistoryScanDevices.set(operation.operationId, {
      deviceId: resolved,
      importFilter: options.importStatus,
      providers: [...providers],
      workspacePath: path,
    });
    return operation;
  }

  async getProviderHistoryScan(
    operationId: string,
    targetOptions?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryScanOperation> {
    const id = operationId.trim();
    if (!id) throw new TypeError("operationId is required");
    const options = providerHistoryTarget(targetOptions);
    const metadata = this.providerHistoryScanDevices.get(id);
    const resolved = metadata?.deviceId ?? await this.resolveDevice(options.deviceId);
    const response = await this.enqueue(resolved, "provider_history.scan.get", null, {
      operation_id: id,
    }, PROVIDER_HISTORY_REQUEST_TIMEOUT_MS);
    const value = response.result
      ?? await this.waitForCommandResult(
        resolved,
        response.command.commandId,
        "read Provider history scan status",
        PROVIDER_HISTORY_REQUEST_TIMEOUT_MS,
      );
    const operation = decodeProviderHistoryScanOperation(value);
    if (!metadata) {
      this.providerHistoryScanDevices.set(id, {
        deviceId: resolved,
        importFilter: options.importStatus,
        providers: [],
        workspacePath: "",
      });
    }
    return operation;
  }

  async cancelProviderHistoryScan(
    operationId: string,
    target?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryScanOperation> {
    const id = operationId.trim();
    if (!id) throw new TypeError("operationId is required");
    const options = providerHistoryTarget(target);
    const metadata = this.providerHistoryScanDevices.get(id);
    const resolved = metadata?.deviceId ?? await this.resolveDevice(options.deviceId);
    const response = await this.enqueue(resolved, "provider_history.scan.cancel", null, {
      operation_id: id,
    }, PROVIDER_HISTORY_REQUEST_TIMEOUT_MS);
    const value = response.result
      ?? await this.waitForCommandResult(
        resolved,
        response.command.commandId,
        "cancel Provider history scan",
        PROVIDER_HISTORY_REQUEST_TIMEOUT_MS,
      );
    return decodeProviderHistoryScanOperation(value);
  }

  async resumeProviderHistoryScan(
    operationId: string,
    target?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryScanOperation> {
    const id = operationId.trim();
    if (!id) throw new TypeError("operationId is required");
    const options = providerHistoryTarget(target);
    const metadata = this.providerHistoryScanDevices.get(id);
    const resolved = metadata?.deviceId ?? await this.resolveDevice(options.deviceId);
    const response = await this.enqueue(resolved, "provider_history.scan.resume", null, {
      operation_id: id,
    }, PROVIDER_HISTORY_REQUEST_TIMEOUT_MS);
    const value = response.result
      ?? await this.waitForCommandResult(
        resolved,
        response.command.commandId,
        "resume Provider history scan",
        PROVIDER_HISTORY_REQUEST_TIMEOUT_MS,
      );
    return decodeProviderHistoryScanOperation(value);
  }

  async pollProviderHistoryScan(
    operationId: string,
    targetOptions?: ProviderHistoryTarget,
    pollOptions: ProviderHistoryScanPollOptions = {},
  ): Promise<ProviderHistoryScanOperation> {
    const timeoutMs = Math.max(1, pollOptions.timeoutMs ?? PROVIDER_HISTORY_REQUEST_TIMEOUT_MS);
    const intervalMs = Math.max(25, pollOptions.intervalMs ?? this.commandReceiptPollIntervalMs);
    const deadline = Date.now() + timeoutMs;
    let first = true;
    while (first || Date.now() < deadline) {
      first = false;
      if (pollOptions.signal?.aborted) {
        throw new OrniTransportError("TIMEOUT", "Provider history scan polling was cancelled.", true);
      }
      const operation = await this.getProviderHistoryScan(operationId, targetOptions);
      if (["complete", "failed", "cancelled"].includes(operation.state)) return operation;
      const remaining = deadline - Date.now();
      if (remaining <= 0) break;
      await delay(Math.min(intervalMs, remaining));
    }
    throw new OrniTransportError(
      "TIMEOUT",
      "Timed out waiting for the Provider history scan.",
      true,
      { operationId, timeoutMs },
    );
  }

  async readProviderConversation(
    input: ProviderHistoryReadInput,
    targetOptions?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryConversationPage> {
    const options = providerHistoryTarget(targetOptions);
    const target = options.deviceId ?? this.selectedDeviceId ?? this.fixedDeviceId;
    const resolved = target ?? (await this.listDevices()).find((device) => device.online)?.deviceId;
    if (!resolved) throw new OrniTransportError("UNAVAILABLE", "No enrolled computer is online.", true);
    const payload = {
      workspace_path: input.workspacePath,
      provider: input.provider,
      conversation_id: input.conversationId,
      history_home_directory: input.historyHomeDirectory,
      cursor: input.cursor ?? 0,
      limit: input.limit ?? 80,
    };
    const read = async (request: Record<string, unknown>): Promise<unknown> => {
      const response = await this.enqueue(
        resolved,
        "provider_history.read",
        null,
        request,
      );
      return response.result ?? await this.waitForCommandResult(
        resolved,
        response.command.commandId,
        "read Provider history",
      );
    };
    let value: unknown;
    try {
      value = await read(payload);
    } catch (cause) {
      // Rolling upgrades may briefly target a Daemon whose read contract does
      // not know the optional history-home field. A read is side-effect free,
      // so retry exactly once with the legacy payload, but only for an
      // explicit non-retryable contract error naming this field. Never retry
      // timeouts or indeterminate command outcomes.
      if (!isHistoryHomeDirectoryCompatibilityError(cause)) throw cause;
      const legacyPayload = { ...payload };
      delete legacyPayload.history_home_directory;
      value = await read(legacyPayload);
    }
    return decodeProviderHistoryConversationPage(value, { kind: "device", deviceId: resolved }, resolved);
  }

  async importProviderHistory(
    conversations: ProviderHistoryImportInput[],
    targetOptions?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryImportResult> {
    const options = providerHistoryTarget(targetOptions);
    const target = options.deviceId ?? this.selectedDeviceId ?? this.fixedDeviceId;
    const resolved = target ?? (await this.listDevices()).find((device) => device.online)?.deviceId;
    if (!resolved) throw new OrniTransportError("UNAVAILABLE", "No enrolled computer is online.", true);
    const response = await this.enqueue(resolved, "provider_history.import", null, {
      conversations: conversations.map((conversation) => ({
        provider: conversation.provider,
        ...(conversation.historyHomeDirectory ? { history_home_directory: conversation.historyHomeDirectory } : {}),
        ...(conversation.sourceId ? { source_id: conversation.sourceId } : {}),
        conversation_id: conversation.conversationId,
        workspace_path: conversation.workspacePath,
        ...(conversation.title ? { title: conversation.title } : {}),
      })),
    }, PROVIDER_HISTORY_REQUEST_TIMEOUT_MS);
    const value = response.result ?? await this.waitForCommandResult(
      resolved,
      response.command.commandId,
      "import Provider history",
      PROVIDER_HISTORY_REQUEST_TIMEOUT_MS,
    );
    for (const key of this.providerHistoryCache.keys()) {
      if (key.startsWith(`${resolved}|`)) this.providerHistoryCache.delete(key);
    }
    return decodeProviderHistoryImportResult(value, { kind: "device", deviceId: resolved });
  }

  async createSession(input: SessionCreateInput): Promise<SessionDetail> {
    const deviceId = input.targetDeviceId ?? this.selectedDeviceId ?? this.fixedDeviceId;
    if (!deviceId) {
      throw new OrniTransportError(
        "UNSUPPORTED",
        "Remote Session creation requires an enrolled target device.",
      );
    }
    const requestedSessionId = input.sessionId?.trim()
      || (this.preallocateSessionId ? this.sessionId() : null);
    // This payload still uses the legacy provider wire contract. Preserve
    // opaque IDs in read projections, but fail explicitly at this execution
    // boundary until a verified Agent adapter is available.
    requireLegacyProviderId(input.provider, "SessionCreateInput.provider");
    const title = input.title?.trim();
    const createOperationId = this.operationId();
    let response: EnqueuedCommand & { result?: unknown };
    try {
      response = await this.enqueue(
        deviceId,
        "sessions.create",
        null,
        createPayload({ ...input, title: title || undefined }, requestedSessionId),
        DEFAULT_REMOTE_COMMAND_TTL_MS,
        createOperationId,
      );
    } catch (error) {
      if (requestedSessionId && isUnknownRemoteCompletion(error)) {
        throw unknownCreateCompletion(error, {
          deviceId,
          requestedSessionId,
          operationId: createOperationId,
        });
      }
      throw error;
    }
    // A caller-assigned identity is useful for the optimistic shell, but it
    // is not proof that the Daemon created anything. The old preallocation
    // path returned this id as soon as Cloud queued the command; a rejected
    // create then opened a phantom Session and waited forever for a terminal.
    // Wait for the create receipt unless the gateway already returned the
    // applied result inline.
    let result: unknown;
    try {
      result = response.result
        ?? await this.waitForCommandResult(deviceId, response.command.commandId, "create the Session");
    } catch (error) {
      if (requestedSessionId && isUnknownRemoteCompletion(error)) {
        throw unknownCreateCompletion(error, {
          deviceId,
          requestedSessionId,
          operationId: createOperationId,
          commandId: response.command.commandId,
        });
      }
      throw error;
    }
    const confirmedSessionId = extractSessionId(result);
    if (confirmedSessionId && requestedSessionId && confirmedSessionId !== requestedSessionId) {
      throw new OrniTransportError("CONTRACT_INVALID", "The target computer confirmed a different Session identity.");
    }
    const sessionId = confirmedSessionId ?? requestedSessionId;
    if (!sessionId) {
      throw new OrniTransportError(
        "TIMEOUT",
        "The computer created the request but has not returned the new Session yet. Refresh the Sessions list and try again.",
        true,
      );
    }
    this.sessionDevices.set(remoteSessionKey(deviceId, sessionId), deviceId);
    let startupFailed = false;
    if (input.startImmediately !== false) {
      // Creation already selected the target computer. Do not require a
      // projection that cannot exist until the first attempt is published.
      const startOperationId = input.startOperationId?.trim() || this.operationId();
      try {
        await this.commandOnDevice(
          deviceId,
          "sessions.start",
          sessionId,
          false,
          {},
          startOperationId,
        );
      } catch (error) {
        // Creation and startup are separate lifecycle phases. A transient
        // start/relay failure must never erase the confirmed Session identity.
        // Preserve that distinction for callers: an unknown start completion
        // needs idempotent recovery, whereas a successful enqueue must not be
        // raced by a second optimistic start request.
        if (isUnknownRemoteCompletion(error)) {
          throw unknownStartCompletion(error, {
            deviceId,
            requestedSessionId: sessionId,
            operationId: startOperationId,
          });
        }
        startupFailed = true;
      }
    }
    const summary = createdSessionSummary(sessionId, deviceId, { ...input, title: title || undefined }, this.now());
    return {
      ...summary,
      ...(startupFailed ? { status: "error" as const, attention: "action" as const } : {}),
      processAttemptId: summary.processAttemptId ?? null,
      permissionMode: input.permissionMode ?? "workspace-write",
    };
  }

  startSession(sessionId: string, input: import("./domain/session.ts").SessionStartInput = {}): Promise<CommandReceipt> {
    return this.enqueueLifecycleCommand(
      sessionId,
      "sessions.start",
      false,
      {},
      input.operationId?.trim() || undefined,
    );
  }

  /**
   * Wait until the owning computer publishes a newer Session projection.
   * Command acceptance is not application: this bounded helper lets a UI
   * distinguish the two without treating the Cloud command row as runtime
   * state.
   */
  async waitForSessionProjectionChange(
    sessionId: string,
    timeoutMs = 20_000,
  ): Promise<boolean> {
    const baselineKey = this.sessionCacheKey(sessionId);
    const hasCapturedBaseline = this.lifecycleProjectionBaselines.has(baselineKey);
    const capturedBaseline = this.lifecycleProjectionBaselines.get(baselineKey);
    this.lifecycleProjectionBaselines.delete(baselineKey);
    const baseline = hasCapturedBaseline
      ? capturedBaseline
      : projectionFingerprint(this.lastProjection(sessionId) ?? null);
    const deadline = Date.now() + Math.max(250, timeoutMs);
    while (Date.now() < deadline) {
      try {
        const projection = await this.refreshProjection(sessionId);
        if (projectionChanged(projection, baseline)) {
          return true;
        }
      } catch {
        // The caller still has the accepted command and the normal UI
        // reconciliation timer. A transient read failure must not turn a
        // successful local command into a false failure.
      }
      await new Promise<void>((resolve) => setTimeout(resolve, 500));
    }
    return false;
  }

  /**
   * Fetch the authoritative terminal receipt for a Cloud command. Realtime
   * events intentionally contain only bounded metadata; callers use this
   * targeted read to recover the full result without refreshing the fleet.
   */
  async getCommandReceiptStatus(
    deviceId: string,
    commandId: string,
  ): Promise<RemoteCommandReceiptStatus> {
    return commandReceiptStatus(await this.http.request(
      `/v1/devices/${encodeURIComponent(deviceId)}/commands/${encodeURIComponent(commandId)}/status`,
    ));
  }

  /**
   * Wait for a remotely queued command to reach a terminal receipt.
   *
   * Lifecycle callers normally wait for the Session projection, but that
   * projection is eventually-consistent with the Daemon command receipt.  A
   * stop can therefore already be applied on the target computer while the
   * Mobile fleet snapshot still reports the old running state.  This bounded
   * status read gives the UI an authoritative completion signal without
   * making the command itself synchronous or opening a second terminal
   * connection.  `null` means the receipt did not settle before the deadline
   * (or the rolling Cloud version does not expose status yet).
   */
  async waitForCommandReceiptTerminal(
    deviceId: string,
    commandId: string,
    timeoutMs = this.commandReceiptTimeoutMs,
  ): Promise<RemoteCommandReceiptStatus | null> {
    const deadline = Date.now() + Math.max(250, timeoutMs);
    let first = true;
    while (first || Date.now() < deadline) {
      first = false;
      try {
        const status = await this.getCommandReceiptStatus(deviceId, commandId);
        if (isTerminalCommandStatus(status)) return status;
      } catch (error) {
        // A command may be accepted before the status projection is readable;
        // preserve the accepted operation and retry until the bounded deadline.
        // Authentication/capability failures are not eventual-consistency
        // conditions; surface them immediately so Mobile can recover its
        // account state instead of leaving a stale stopping banner.
        if (error instanceof OrniTransportError
          && ["UNAUTHENTICATED", "PERMISSION_DENIED"].includes(error.code)) {
          throw error;
        }
      }
      const remaining = deadline - Date.now();
      if (remaining <= 0) break;
      await delay(Math.min(this.commandReceiptPollIntervalMs, remaining));
    }
    return null;
  }

  async getSyncStatus(): Promise<SyncStatus> {
    return decodeSyncStatus(await this.http.request("/v1/sync/status"));
  }

  /** Wait on the dedicated, authenticated receipt stream for one command.
   * The first event is the current authoritative status, so reconnecting after
   * the receipt committed cannot miss completion. */
  async waitForCommandReceiptStatus(
    deviceId: string,
    commandId: string,
    signal?: AbortSignal,
  ): Promise<RemoteCommandReceiptStatus> {
    const response = await this.http.openStream(
      `/v1/devices/${encodeURIComponent(deviceId)}/commands/${encodeURIComponent(commandId)}/status/stream`,
      signal,
    );
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    const cancelReader = () => {
      void reader.cancel().catch(() => undefined);
    };
    if (signal?.aborted) {
      cancelReader();
      throw new OrniTransportError("TIMEOUT", "The Cloud event stream was cancelled.", true);
    }
    signal?.addEventListener("abort", cancelReader, { once: true });
    try {
      while (true) {
        const result = await reader.read();
        buffer += decoder.decode(result.value ?? new Uint8Array(), { stream: !result.done });
        let next = takeSseBlock(buffer);
        while (next) {
          const { block } = next;
          buffer = next.rest;
          next = takeSseBlock(buffer);
          const data = block
            .split(/\r?\n/)
            .filter((line) => line.startsWith("data:"))
            .map((line) => line.slice(5).replace(/^ /, ""));
          if (data.length) {
            try {
              const status = commandReceiptStatus(JSON.parse(data.join("\n")) as unknown);
              if (isTerminalCommandStatus(status)) return status;
            } catch {
              throw new OrniTransportError(
                "UNAVAILABLE",
                "The command receipt stream returned invalid data.",
                true,
              );
            }
          }
        }
        if (result.done) {
          throw new OrniTransportError(
            "UNAVAILABLE",
            "The command receipt stream closed before a terminal result.",
            true,
          );
        }
      }
    } finally {
      signal?.removeEventListener("abort", cancelReader);
      await reader.cancel().catch(() => undefined);
    }
  }

  pauseSession(sessionId: string, waitForCompletion = false): Promise<CommandReceipt> {
    return this.command("sessions.pause", sessionId, waitForCompletion);
  }

  recoverSession(sessionId: string, input: SessionRecoveryInput = {}): Promise<CommandReceipt> {
    return this.command("sessions.recover", sessionId, false, {
      ...(input.incidentId ? { incident_id: input.incidentId } : {}),
      action: input.action ?? "continue",
    });
  }

  stopSession(sessionId: string): Promise<CommandReceipt> {
    return this.enqueueLifecycleCommand(sessionId, "sessions.stop", false);
  }

  restartSession(sessionId: string, waitForCompletion = false): Promise<CommandReceipt> {
    return this.enqueueLifecycleCommand(sessionId, "sessions.restart", waitForCompletion);
  }

  private async enqueueLifecycleCommand(
    sessionId: string,
    operation: "sessions.start" | "sessions.stop" | "sessions.restart",
    waitForCompletion: boolean,
    payload: Record<string, unknown> = {},
    operationId?: string,
  ): Promise<CommandReceipt> {
    // Capture the pre-command projection before command() refreshes it while
    // resolving the owning device. The UI watcher can then distinguish an
    // applied command from the refresh performed during command dispatch.
    const cachedBaseline = this.lastProjection(sessionId);
    let baselineKey = this.sessionCacheKey(sessionId);
    if (cachedBaseline) {
      this.lifecycleProjectionBaselines.set(baselineKey, projectionFingerprint(cachedBaseline));
    } else {
      try {
        const baselineProjection = await this.refreshProjection(sessionId);
        if (baselineProjection?.deviceId) {
          baselineKey = remoteSessionKey(baselineProjection.deviceId, sessionId);
        }
        this.lifecycleProjectionBaselines.set(
          baselineKey,
          projectionFingerprint(baselineProjection),
        );
      } catch {
        // Command dispatch still has its normal cached-device fallback. A read
        // outage must not prevent a lifecycle command from being accepted.
        this.lifecycleProjectionBaselines.set(baselineKey, projectionFingerprint(this.lastProjection(sessionId) ?? null));
      }
    }
    try {
      return await this.command(operation, sessionId, waitForCompletion, payload, operationId);
    } catch (error) {
      this.lifecycleProjectionBaselines.delete(baselineKey);
      throw error;
    }
  }

  renameSession(sessionId: string, title: string): Promise<CommandReceipt> {
    const normalized = title.trim();
    if (!normalized) {
      throw new OrniTransportError("INVALID_ARGUMENT", "Session title cannot be empty.");
    }
    return this.command("sessions.rename", sessionId, true, { title: normalized });
  }

  deleteSession(sessionId: string): Promise<CommandReceipt> {
    return this.command("sessions.delete", sessionId, true, {
      delete_durable_work_records: false,
    });
  }

  setAutoContinuePolicy(sessionId: string, policy: unknown): Promise<CommandReceipt> {
    return this.command("sessions.set_auto_continue", sessionId, true, { policy });
  }

  private async command(
    operation: CommandReceipt["command"],
    sessionId: string,
    waitForCompletion = false,
    payload: Record<string, unknown> = {},
    operationId?: string,
  ): Promise<CommandReceipt> {
    // Session creation has no projection yet. All other lifecycle commands
    // opportunistically refresh the target, while retaining the cached device
    // only for rolling Cloud versions that do not expose the direct lookup.
    const projection = operation === "sessions.create"
      ? null
      : await this.tryRefreshProjection(sessionId);
    const deviceId = projection?.deviceId ?? await this.resolveSessionDevice(sessionId);
    return this.commandOnDevice(deviceId, operation, sessionId, waitForCompletion, payload, operationId);
  }

  /**
   * Dispatch to a device whose identity is already authoritative for this
   * operation. This is essential for create -> initial-start: no Session
   * projection exists until the Daemon has started the new runtime.
   */
  private async commandOnDevice(
    deviceId: string,
    operation: CommandReceipt["command"],
    sessionId: string,
    waitForCompletion = false,
    payload: Record<string, unknown> = {},
    operationId?: string,
  ): Promise<CommandReceipt> {
    const result = await this.enqueue(
      deviceId,
      operation,
      sessionId,
      payload,
      DEFAULT_REMOTE_COMMAND_TTL_MS,
      operationId?.trim() || this.operationId(),
    );
    const accepted: CommandReceipt = {
      id: result.command.commandId,
      command: operation,
      status: "accepted",
      requestedAt: result.command.createdAt,
      completedAt: null,
      detail: "Cloud accepted the command; the target computer has not applied it yet.",
    };
    return waitForCompletion
      ? this.waitForCommandReceipt(deviceId, accepted)
      : accepted;
  }

  private deviceFor(sessionId: string): string {
    if (this.fixedDeviceId) return this.fixedDeviceId;
    const candidates = this.sessionDevicesFor(sessionId);
    if (this.selectedDeviceId && candidates.includes(this.selectedDeviceId)) return this.selectedDeviceId;
    if (candidates.length === 1) return candidates[0];
    if (candidates.length > 1) {
      throw new OrniTransportError(
        "INVALID_ARGUMENT",
        "The Session id is present on multiple computers. Select the owning computer before sending a command.",
      );
    }
    throw new OrniTransportError("INVALID_ARGUMENT", "The Session device is unknown or ambiguous. Refresh the remote Session list and select its computer before sending a command.");
  }

  /**
   * Resolve a Session owner even when this transport was created after the
   * session list was loaded (the normal Mobile refresh/deep-link path).
   * Cloud's projection is authoritative; a successful read also repopulates
   * the local mapping so subsequent command paths stay synchronous and cheap.
   */
  private async resolveSessionDevice(sessionId: string): Promise<string> {
    if (this.fixedDeviceId) return this.fixedDeviceId;
    try {
      return this.deviceFor(sessionId);
    } catch (error) {
      if (!(error instanceof OrniTransportError) || error.code !== "INVALID_ARGUMENT") throw error;
    }
    const projection = await this.refreshProjection(sessionId);
    if (projection?.deviceId) return projection.deviceId;
    // Preserve the specific ambiguity error when the identity exists on more
    // than one computer; a missing Session is reported as NOT_FOUND instead
    // of the misleading cache-oriented "unknown or ambiguous" message.
    const candidates = this.sessionDevicesFor(sessionId);
    if (candidates.length > 1 && !this.preferredDeviceFor(sessionId)) {
      throw new OrniTransportError(
        "INVALID_ARGUMENT",
        "The Session id is present on multiple computers. Select the owning computer before sending a command.",
      );
    }
    throw new OrniTransportError("NOT_FOUND", "The remote Session was not found.");
  }

  private async resolveDevice(deviceId?: string): Promise<string> {
    const target = deviceId ?? this.selectedDeviceId ?? this.fixedDeviceId;
    const resolved = target ?? (await this.listDevices()).find((device) => device.online)?.deviceId;
    if (!resolved) throw new OrniTransportError("UNAVAILABLE", "No enrolled computer is online.", true);
    return resolved;
  }

  private lastProjection(sessionId: string): CloudProjection | undefined {
    const preferred = this.preferredDeviceFor(sessionId);
    if (preferred) return this.sessionProjections.get(remoteSessionKey(preferred, sessionId));
    const matches = [...this.sessionProjections.entries()]
      .filter(([key]) => key.endsWith(`\u0000${sessionId}`));
    return matches.length === 1 ? matches[0][1] : undefined;
  }

  private async refreshProjection(sessionId: string): Promise<CloudProjection | null> {
    const knownDevices = this.sessionDevicesFor(sessionId);
    if (knownDevices.length > 1 && !this.preferredDeviceFor(sessionId)) {
      throw new OrniTransportError(
        "INVALID_ARGUMENT",
        "The Session id is present on multiple computers. Select the owning computer before opening it.",
      );
    }
    // `selectedDeviceId` is only a creation/list filter. It is not evidence
    // that an existing Session belongs to that computer. When no cached owner
    // exists, query Cloud globally by session id so switching computers or a
    // deep link cannot route commands to the wrong Daemon.
    const preferred = this.preferredDeviceFor(sessionId) ?? this.fixedDeviceId;
    const params = new URLSearchParams({ limit: "1", sessionId });
    if (preferred) params.set("deviceId", preferred);
    let projections = projectionArray(
      await this.http.request(`/v1/session-projections?${params}`),
    );
    // During a rolling upgrade an older Cloud ignores sessionId and returns
    // its newest projection. Fall back to the legacy bounded scan only when
    // the response proves that happened; a real empty direct result remains a
    // definitive miss on the new endpoint.
    if (projections.length && !projections.some((projection) => projection.sessionId === sessionId)) {
      projections = projectionArray(
        await this.http.request("/v1/session-projections?limit=100"),
      );
    }
    this.rememberProjections(projections);
    const selected = projections.find((projection) => projection.sessionId === sessionId
      && (!preferred || projection.deviceId === preferred))
      ?? (preferred ? null : projections.find((projection) => projection.sessionId === sessionId))
      ?? null;
    if (!selected) {
      this.deleteProjectionEntries(sessionId, preferred);
      return null;
    }
    // rememberProjections refuses to regress a newer cached revision when a
    // request lands on a lagging Cloud replica. Return that canonical row as
    // well: returning the stale response here could mint a terminal ticket for
    // a superseded process attempt even though the cache knew the current one.
    return this.sessionProjections.get(remoteSessionKey(selected.deviceId, selected.sessionId)) ?? selected;
  }

  private async tryRefreshProjection(sessionId: string): Promise<CloudProjection | null> {
    try {
      return await this.refreshProjection(sessionId);
    } catch (error) {
      // A rolling Cloud deployment may not understand the identity query,
      // and a transient read outage should not prevent a command from being
      // delivered to the device already associated with this Session. Do not
      // swallow authentication or capability errors.
      if (error instanceof OrniTransportError && ["CONTRACT_INVALID", "NOT_FOUND", "UNAVAILABLE", "TIMEOUT"].includes(error.code)) {
        return this.lastProjection(sessionId) ?? null;
      }
      throw error;
    }
  }

  private async listProjectionPages(): Promise<CloudProjection[]> {
    const collected = new Map<string, CloudProjection>();
    let after: CloudProjection | null = null;
    while (true) {
      const params = new URLSearchParams({
        limit: String(SESSION_PROJECTION_PAGE_SIZE),
        page: "true",
      });
      if (after) {
        params.set("afterDeviceId", after.deviceId);
        params.set("afterSessionId", after.sessionId);
      }
      const page = projectionArray(
        await this.http.request(`/v1/session-projections?${params}`),
      );
      this.rememberProjections(page);
      for (const projection of page) {
        collected.set(`${projection.deviceId}\u0000${projection.sessionId}`, projection);
      }
      if (page.length < SESSION_PROJECTION_PAGE_SIZE) break;
      const next = page.at(-1)!;
      // An older Cloud ignores the new paging fields and repeats its first
      // bounded page. Stop safely during rolling upgrades instead of spinning.
      if (
        after
        && next.deviceId === after.deviceId
        && next.sessionId === after.sessionId
      ) {
        break;
      }
      after = next;
    }
    return [...collected.values()];
  }

  private async listProjectionWindow(limit: number, offset: number): Promise<CloudProjection[]> {
    const projections: CloudProjection[] = [];
    let nextOffset = offset;
    while (projections.length < limit) {
      const pageLimit = Math.min(SESSION_PROJECTION_PAGE_SIZE, limit - projections.length);
      const params = new URLSearchParams({
        limit: String(pageLimit),
      });
      // The first window is the natural Cloud query and must not carry an
      // unnecessary offset=0. Besides producing cleaner URLs, this keeps the
      // request compatible with older Daemon gateways whose typed allowlist
      // predates the offset parameter. Subsequent windows still use offset
      // to preserve the activity ordering contract.
      if (nextOffset > 0) params.set("offset", String(nextOffset));
      const page = projectionArray(await this.http.request(
        `/v1/session-projections?${params}`,
      ));
      projections.push(...page);
      if (page.length < pageLimit) break;
      nextOffset += page.length;
    }
    return projections;
  }

  private rememberProjections(projections: readonly CloudProjection[]): void {
    for (const projection of projections) {
      const key = remoteSessionKey(projection.deviceId, projection.sessionId);
      this.sessionDevices.set(key, projection.deviceId);
      const current = this.sessionProjections.get(key);
      // Cloud reads can be routed to a lagging replica. Never regress a
      // cached projection that already has a newer device revision.
      if (!current || projection.revision >= current.revision) {
        this.sessionProjections.set(key, projection);
      }
    }
  }

  private sessionDevicesFor(sessionId: string): string[] {
    const suffix = `\u0000${sessionId}`;
    return [...new Set([...this.sessionDevices.entries()]
      .filter(([key]) => key.endsWith(suffix))
      .map(([, deviceId]) => deviceId))];
  }

  private preferredDeviceFor(sessionId: string): string | undefined {
    if (this.fixedDeviceId) return this.fixedDeviceId;
    const candidates = this.sessionDevicesFor(sessionId);
    if (this.selectedDeviceId && candidates.includes(this.selectedDeviceId)) return this.selectedDeviceId;
    return candidates.length === 1 ? candidates[0] : undefined;
  }

  private sessionCacheKey(sessionId: string): string {
    const deviceId = this.preferredDeviceFor(sessionId);
    return remoteSessionKey(deviceId ?? "?", sessionId);
  }

  private deleteProjectionEntries(sessionId: string, deviceId?: string): void {
    if (deviceId) {
      const key = remoteSessionKey(deviceId, sessionId);
      this.sessionProjections.delete(key);
      this.sessionDevices.delete(key);
      return;
    }
    const suffix = `\u0000${sessionId}`;
    for (const key of [...this.sessionProjections.keys()]) {
      if (key.endsWith(suffix)) this.sessionProjections.delete(key);
    }
    for (const key of [...this.sessionDevices.keys()]) {
      if (key.endsWith(suffix)) this.sessionDevices.delete(key);
    }
  }

  private async waitForProjection(sessionId: string, deadlineAt: number | null = null): Promise<CloudProjection | null> {
    const deadline = deadlineAt ?? Date.now() + this.projectionWaitTimeoutMs;
    let projection: CloudProjection | null = this.lastProjection(sessionId) ?? null;
    let reads = 0;
    while (!projection?.processAttemptId && Date.now() < deadline) {
      projection = await withDeadline(
        this.refreshProjection(sessionId),
        deadlineAt,
        "Timed out while resolving the remote Session projection.",
      );
      reads += 1;
      if (projection?.processAttemptId) return projection;
      const remaining = deadline - Date.now();
      if (remaining <= 0) break;
      await delay(Math.min(this.commandReceiptPollIntervalMs, remaining));
    }
    // Timers are not precise under browser or test-runner load. Always make a
    // second authoritative read when the first page had no attempt; a slow
    // event loop must not turn a just-published attempt into a false outage.
    if (!projection?.processAttemptId && reads < 2) {
      projection = await withDeadline(
        this.refreshProjection(sessionId),
        deadlineAt,
        "Timed out while resolving the remote Session projection.",
      );
    }
    return projection ?? null;
  }

  private async waitForActiveTerminalProjection(
    sessionId: string,
    initial: CloudProjection | null,
    deadlineAt: number | null = null,
  ): Promise<CloudProjection | null> {
    let projection = initial;
    const deadline = deadlineAt ?? Date.now() + this.projectionWaitTimeoutMs;
    while (Date.now() < deadline) {
      if (projection?.processAttemptId
        && ["running", "waiting"].includes(projection.status)) return projection;
      // Inactive Sessions should return immediately so the caller can render
      // its explicit Start action. Only a `starting` projection gets a bounded
      // wait: it may still contain the previous attempt id while the Daemon
      // publishes the new one.
      if (projection && ["stopped", "exited", "error", "archived"].includes(projection.status)) return projection;
      const remaining = deadline - Date.now();
      if (remaining <= 0) break;
      await delay(Math.min(this.commandReceiptPollIntervalMs, remaining));
      projection = await withDeadline(
        this.refreshProjection(sessionId),
        deadlineAt,
        "Timed out while refreshing the terminal projection.",
      ).catch(() => projection);
    }
    return projection;
  }

  private async enqueue(
    deviceId: string,
    operation: CommandReceipt["command"],
    sessionId: string | null,
    payload: Record<string, unknown>,
    ttlMs = DEFAULT_REMOTE_COMMAND_TTL_MS,
    operationId = this.operationId(),
    unwrapResult = true,
  ): Promise<EnqueuedCommand & { result?: unknown }> {
    this.assertDeviceOperation(deviceId, operation);
    const requestedAt = this.now();
    return commandResult(await this.http.request(
      `/v1/devices/${encodeURIComponent(deviceId)}/commands`,
      {
        method: "POST",
        body: JSON.stringify({
          idempotencyKey: operationId,
          kind: "orni.command.v1",
          payload: commandEnvelope(operationId, operation, sessionId, payload, requestedAt, ttlMs),
          expiresAt: new Date(requestedAt.getTime() + ttlMs).toISOString(),
        }),
      },
    ), unwrapResult);
  }

  private assertDeviceOperation(deviceId: string, operation: string): void {
    const capabilities = this.deviceCapabilities.get(deviceId);
    if (!capabilities) return;
    const advertised = capabilities.operations;
    if (!Array.isArray(advertised)) return;
    const supported = advertised.some((item) => {
      if (typeof item === "string") return item === operation;
      if (!item || typeof item !== "object" || Array.isArray(item)) return false;
      const row = item as Record<string, unknown>;
      return row.operation === operation;
    });
    if (!supported) {
      throw new OrniTransportError(
        "UNSUPPORTED",
        `The target computer does not support ${operation}. Update its Orni Daemon before trying again.`,
        false,
        { deviceId, operation, capabilities },
      );
    }
  }

  private async waitForCommandReceipt(
    deviceId: string,
    accepted: CommandReceipt,
  ): Promise<CommandReceipt> {
    const deadline = Date.now() + this.commandReceiptTimeoutMs;
    const streamed = await this.waitForReceiptStream(deviceId, accepted.id, deadline);
    if (streamed) {
      const terminal = terminalCommandReceipt(streamed, accepted.command);
      if (terminal) {
        const outcome = streamed.outcome ?? streamed.status;
        if (outcome !== "applied") {
          throw remoteReceiptError(streamed, `apply ${accepted.command}`);
        }
        return terminal;
      }
    }
    // The event loop may resume after both the stream window and the nominal
    // deadline under heavy client load. Always perform one authoritative
    // status read after the stream fallback instead of fabricating `running`.
    let polled = false;
    while (!polled || Date.now() < deadline) {
      polled = true;
      const status = await this.getCommandReceiptStatus(deviceId, accepted.id);
      const terminal = terminalCommandReceipt(status, accepted.command);
      if (terminal) {
        const outcome = status.outcome ?? status.status;
        if (outcome !== "applied") {
          throw remoteReceiptError(status, `apply ${accepted.command}`);
        }
        return terminal;
      }
      await delay(this.commandReceiptPollIntervalMs);
    }
    return {
      ...accepted,
      status: "running",
      detail: "Cloud accepted the command; the target computer is still processing it.",
    };
  }

  private async waitForCommandResult(
    deviceId: string,
    commandId: string,
    operationLabel = "complete the command",
    timeoutMs = this.commandReceiptTimeoutMs,
  ): Promise<unknown> {
    const deadline = Date.now() + timeoutMs;
    const streamed = await this.waitForReceiptStream(deviceId, commandId, deadline);
    if (streamed) {
      const outcome = streamed.outcome ?? streamed.status;
      if (outcome === "applied") return unwrapDaemonCommandResult(streamed.result);
      if (["rejected", "failed", "indeterminate", "expired"].includes(outcome)) {
        throw remoteReceiptError(streamed, operationLabel);
      }
    }
    let polled = false;
    while (!polled || Date.now() < deadline) {
      polled = true;
      const status = await this.getCommandReceiptStatus(deviceId, commandId);
      const outcome = status.outcome ?? status.status;
      if (outcome === "applied") return unwrapDaemonCommandResult(status.result);
      if (["rejected", "failed", "indeterminate", "expired"].includes(outcome)) {
        throw remoteReceiptError(status, operationLabel);
      }
      await delay(this.commandReceiptPollIntervalMs);
    }
    throw new OrniTransportError(
      "TIMEOUT",
      `Timed out waiting for the target computer to ${operationLabel}.`,
      true,
      { deviceId, commandId, timeoutMs },
    );
  }

  private async waitForReceiptStream(
    deviceId: string,
    commandId: string,
    deadline: number,
  ): Promise<RemoteCommandReceiptStatus | null> {
    if (!this.commandReceiptStreamEnabled) return null;
    const remaining = deadline - Date.now();
    if (remaining <= 0) return null;
    const controller = new AbortController();
    // The targeted SSE normally closes immediately when PostgreSQL publishes
    // relay.command.closed. Treat it as an acceleration path, not an exclusive
    // five-second wait: a missed database notification is recovered by the
    // authoritative status endpoint without adding Cloud's full refresh period
    // to every dependent command (notably create -> start).
    const streamWindow = Math.min(
      Math.max(1, Math.floor(remaining / 2)),
      Math.max(250, Math.min(750, this.commandReceiptPollIntervalMs * 2)),
    );
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const stream = this.waitForCommandReceiptStatus(deviceId, commandId, controller.signal);
    // Abort is the normal cancellation path, but a custom fetch implementation
    // may ignore AbortSignal. Race the stream explicitly so receipt polling
    // always retains the second half of its deadline.
    const timeout = new Promise<null>((resolve) => {
      timeoutId = setTimeout(() => {
        controller.abort();
        resolve(null);
      }, streamWindow);
    });
    try {
      return await Promise.race([stream, timeout]);
    } catch (error) {
      // Older Cloud versions do not expose the stream. Preserve rolling
      // upgrade compatibility by falling back to the bounded status poll.
      if (error instanceof OrniTransportError
        && ["UNAUTHENTICATED", "PERMISSION_DENIED"].includes(error.code)) {
        throw error;
      }
      return null;
    } finally {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      controller.abort();
      // Do not leave a rejected stream promise unhandled after the timeout won.
      void stream.catch(() => undefined);
    }
  }

}

function projectRemote(projection: CloudProjection, now: Date): SessionSummary {
  const authority = { kind: "device" as const, deviceId: projection.deviceId };
  const projectedAt = Date.parse(projection.projectedAt);
  const stale = !Number.isFinite(projectedAt) || now.getTime() - projectedAt > 90_000;
  // `status` and `attention` are Desktop Daemon projections. Cloud freshness
  // is transport metadata, not process state: a healthy session can remain
  // unchanged for longer than the projection refresh window. Never invent an
  // error merely because Cloud has not received a duplicate heartbeat.
  const status = projection.status;
  const attention = projection.attention;
  return {
    ref: { resourceType: "session", resourceId: projection.sessionId, authority },
    id: projection.sessionId,
    shortId: projection.sessionId.replace(/^ses_/, "").slice(0, 8),
    deviceName: projection.deviceName,
    title: projection.title,
    provider: projection.provider,
    agentId: projection.agentId,
    cliVariantId: projection.cliVariantId,
    managementMode: "native",
    model: projection.model,
    status,
    providerReadiness: projection.providerReadiness,
    attention,
    visibility: projection.visibility,
    workspace: {
      id: projection.workspaceId,
      name: projection.workspaceLabel,
      path: "",
      branch: projection.branch,
    },
    createdAt: projection.createdAt,
    updatedAt: projection.updatedAt,
    lastActivityAt: projection.lastActivityAt,
    unreadCount: 0,
    processAttemptId: projection.processAttemptId,
    generation: projection.generation,
    activeStageId: null,
    taskProgress: null,
    autoContinue: { ...emptyAutoContinue, recoveryHistory: [] },
    controller: {
      mode: "unavailable",
      holderLabel: null,
      leaseId: null,
      expiresAt: null,
      generation: projection.generation,
    },
    preview: projection.preview,
    tags: [...projection.tags, `device:${projection.deviceId}`],
    source: { authority, syncState: stale ? "stale" : "current" },
  };
}

function mapRemoteRecoveryIncident(value: unknown, deviceId: string, deviceName?: string): RemoteRecoveryIncident {
  const row = record(value);
  const restartCause = textOr(row.restartCause ?? row.restart_cause, "unexpected_restart");
  const classification = textOr(row.classification, "interrupted_work");
  const confidence = textOr(row.confidence, "low");
  const status = textOr(row.status, "open");
  return {
    incidentId: textOr(row.incidentId ?? row.incident_id, ""),
    deviceId,
    ...(deviceName ? { deviceName } : {}),
    sessionId: textOr(row.sessionId ?? row.session_id, ""),
    processAttemptId: textOr(row.processAttemptId ?? row.process_attempt_id, ""),
    previousGeneration: integerOr(row.previousGeneration ?? row.previous_generation, 1),
    sessionTitle: textOr(row.sessionTitle ?? row.session_title, "Untitled Session"),
    providerId: textOr(row.providerId ?? row.provider_id, "unknown"),
    workspaceId: textOr(row.workspaceId ?? row.workspace_id, ""),
    managementMode: textOr(row.managementMode ?? row.management_mode, "native") === "orni_work" ? "orni_work" : "native",
    restartCause: restartCause === "planned_restart" || restartCause === "upgrade" ? restartCause : "unexpected_restart",
    classification: classification === "idle" || classification === "uncertain" ? classification : "interrupted_work",
    confidence: confidence === "high" || confidence === "medium" ? confidence : "low",
    evidence: stringArrayOr(row.evidence),
    lastActivityAt: nullableTextOr(row.lastActivityAt ?? row.last_activity_at),
    detectedAt: textOr(row.detectedAt ?? row.detected_at, new Date(0).toISOString()),
    status: ["open", "continue_requested", "continue_dispatching", "continued", "restored", "dismissed", "superseded"].includes(status)
      ? status as RemoteRecoveryIncident["status"]
      : "open",
    resolvedAt: nullableTextOr(row.resolvedAt ?? row.resolved_at),
    detail: nullableTextOr(row.detail),
  };
}

function createdSessionSummary(
  sessionId: string,
  deviceId: string,
  input: SessionCreateInput,
  now: Date,
): SessionSummary {
  const authority = { kind: "device" as const, deviceId };
  const timestamp = now.toISOString();
  const workspaceId = input.workspaceId ?? "";
  return {
    ref: { resourceType: "session", resourceId: sessionId, authority },
    id: sessionId,
    shortId: sessionId.replace(/^ses_/, "").slice(0, 8),
    deviceName: deviceId,
    title: input.title?.trim() || `${input.provider} session`,
    provider: input.provider,
    managementMode: input.managementMode ?? "native",
    model: input.model ?? (input.provider === "codex" ? "native_app_server" : "terminal_tui"),
    status: input.startImmediately === false ? "stopped" : "starting",
    attention: "none",
    visibility: "background",
    workspace: { id: workspaceId, name: workspaceId.split(/[\\/]/).filter(Boolean).at(-1) ?? workspaceId, path: "", branch: null },
    createdAt: timestamp,
    updatedAt: timestamp,
    lastActivityAt: timestamp,
    unreadCount: 0,
    processAttemptId: null,
    generation: 1,
    activeStageId: null,
    taskProgress: null,
    autoContinue: { ...emptyAutoContinue, recoveryHistory: [] },
    controller: { mode: "unavailable", holderLabel: null, leaseId: null, expiresAt: null, generation: 1 },
    preview: "",
    tags: [`device:${deviceId}`],
    source: { authority, syncState: "stale" },
  };
}

function filterSessions(sessions: SessionSummary[], query: SessionListQuery): SessionSummary[] {
  const search = query.search?.trim().toLocaleLowerCase();
  return sessions.filter((session) => {
    if (!query.includeArchived && session.status === "archived") return false;
    if (query.providers?.length && !query.providers.includes(session.provider)) return false;
    if (query.statuses?.length && !query.statuses.includes(session.status)) return false;
    if (query.attention?.length && !query.attention.includes(session.attention)) return false;
    if (query.workspaceIds?.length && !query.workspaceIds.includes(session.workspace.id)) return false;
    return !search || [session.title, session.preview, session.workspace.name]
      .some((value) => value.toLocaleLowerCase().includes(search));
  });
}

function sortSessions(
  sessions: SessionSummary[],
  sort: SessionListQuery["sort"] = "activity-desc",
): SessionSummary[] {
  return sessions.slice().sort((left, right) => {
    if (sort === "title-asc") return left.title.localeCompare(right.title);
    if (sort === "created-desc") return Date.parse(right.createdAt) - Date.parse(left.createdAt);
    const delta = Date.parse(right.lastActivityAt) - Date.parse(left.lastActivityAt);
    return sort === "activity-asc" ? -delta : delta;
  });
}

function projectionArray(value: unknown): CloudProjection[] {
  if (!Array.isArray(value)) throw contractError("Session projection response must be an array.");
  return value.map((item) => {
    const row = record(item);
    const provider = decodeProviderId(row.provider, "SessionProjection.provider");
    const identity = decodeAgentIdentity({
      provider_id: provider,
      ...((row.agentId ?? row.agent_id) === undefined ? {} : { agent_id: row.agentId ?? row.agent_id }),
      ...((row.cliVariantId ?? row.cli_variant_id) === undefined ? {} : { cli_variant_id: row.cliVariantId ?? row.cli_variant_id }),
    }, "SessionProjection.identity");
    const managementMode = text(row.managementMode);
    const status = text(row.status);
    const attention = text(row.attention);
    const visibility = text(row.visibility);
    if (!isManagementMode(managementMode)
      || !isStatus(status) || !isAttention(attention) || !isVisibility(visibility)) {
      throw contractError("Session projection contains an unsupported enum value.");
    }
    return {
      deviceId: text(row.deviceId), deviceName: text(row.deviceName),
      sessionId: text(row.sessionId), revision: integer(row.revision), provider,
      managementMode, title: text(row.title), model: text(row.model), status,
      providerReadiness: optionalProviderReadiness(row.providerReadiness ?? row.provider_readiness),
      attention, visibility, workspaceId: text(row.workspaceId),
      workspaceLabel: text(row.workspaceLabel), branch: nullableText(row.branch),
      agentId: identity.agentId, cliVariantId: identity.cliVariantId,
      processAttemptId: row.processAttemptId === undefined || row.processAttemptId === null
        ? null
        : nullableText(row.processAttemptId),
      generation: integer(row.generation), preview: text(row.preview),
      tags: stringArray(row.tags), createdAt: text(row.createdAt),
      updatedAt: text(row.updatedAt), lastActivityAt: text(row.lastActivityAt),
      projectedAt: text(row.projectedAt),
    };
  });
}

function optionalProviderReadiness(value: unknown): CloudProjection["providerReadiness"] {
  if (value === undefined || value === null || value === "") return undefined;
  if (value === "unknown" || value === "probing" || value === "ready" || value === "degraded" || value === "unavailable") return value;
  throw contractError("Session projection contains an unsupported provider readiness value.");
}

function remoteSessionKey(deviceId: string, sessionId: string): string {
  return `${deviceId}\u0000${sessionId}`;
}

function commandResult(value: unknown, unwrapResult = true): EnqueuedCommand {
  const root = record(value);
  const command = record(root.command);
  return {
    command: {
      commandId: text(command.commandId),
      createdAt: text(command.createdAt),
      status: text(command.status),
    },
    ...(root.result === undefined
      ? {}
      : { result: unwrapResult ? unwrapDaemonCommandResult(root.result) : root.result }),
  };
}

function directoryPickPath(value: unknown): string | null {
  const row = record(value);
  if (row.path === null) return null;
  if (typeof row.path !== "string" || !row.path.trim()) {
    throw new OrniTransportError(
      "CONTRACT_INVALID",
      "The target computer returned an invalid directory selection.",
    );
  }
  return row.path;
}

function directoryListing(value: unknown): RemoteDirectoryListing {
  const row = record(value);
  const currentPath = text(row.current_path);
  const homePath = text(row.home_path);
  const parentPath = row.parent_path === undefined || row.parent_path === null
    ? null
    : text(row.parent_path);
  if (!currentPath.trim() || !homePath.trim() || !Array.isArray(row.directories) || typeof row.truncated !== "boolean") {
    throw new OrniTransportError("CONTRACT_INVALID", "The target computer returned an invalid directory listing.");
  }
  const directories = row.directories.map((value) => {
    const entry = record(value);
    const name = text(entry.name);
    const path = text(entry.path);
    if (!name.trim() || !path.trim() || entry.kind !== "directory") {
      throw new OrniTransportError("CONTRACT_INVALID", "The target computer returned an invalid directory entry.");
    }
    return { name, path, kind: "directory" as const };
  });
  return { currentPath, parentPath, homePath, directories, truncated: row.truncated };
}

/**
 * Cloud receipts store the Daemon's command execution envelope as their
 * result. Older tests and rolling-upgrade peers may still return the business
 * value directly, so unwrap only when the command identity markers are
 * present. Keeping this at the transport boundary gives every feature the
 * same business payload instead of making each page understand relay layers.
 */
function unwrapDaemonCommandResult(value: unknown): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const row = value as Record<string, unknown>;
  const isEnvelope =
    "result" in row &&
    typeof row.status === "string" &&
    (typeof row.requestId === "string" || typeof row.commandId === "string");
  if (!isEnvelope) return value;
  if (["rejected", "failed", "indeterminate", "expired"].includes(row.status as string)) {
    const error = extractReceiptError(row.error ?? row.result);
    const daemonEnvelope = extractDaemonReceiptEnvelope(value);
    throw new OrniTransportError(
      error ? mapReceiptErrorCode(error.code) : "UNAVAILABLE",
      daemonEnvelope?.status === "indeterminate"
        ? `The target computer returned an indeterminate result while applying the command.`
        : error?.message ?? "The target computer rejected the command.",
      daemonEnvelope?.status === "indeterminate" || error?.retryable === true,
      {
        ...(error ?? {}),
        ...(daemonEnvelope ? {
          daemonStatus: daemonEnvelope.status,
          ...(daemonEnvelope.commandId ? { daemonCommandId: daemonEnvelope.commandId } : {}),
          ...(daemonEnvelope.requestId ? { daemonRequestId: daemonEnvelope.requestId } : {}),
        } : {}),
      },
    );
  }
  return row.result ?? null;
}

function createPayload(input: SessionCreateInput, sessionId: string | null): Record<string, unknown> {
  return {
    ...(sessionId ? { session_id: sessionId } : {}),
    ...(input.title ? { title: input.title } : {}),
    provider: {
      provider_id: input.provider === "claude" ? "claude-code" : input.provider,
      integration_mode: input.provider === "codex" ? "native_app_server" : "terminal_tui",
    },
    workspace: { workspace_id: input.workspaceId ?? "" },
    permission_mode: input.permissionMode ?? "workspace-write",
    management_mode: "native",
    ...(input.workspaceMode
      ? {
          workspace_directory: {
            mode: input.workspaceMode === "session-id" ? "session_id" : input.workspaceMode,
            ...(input.workspaceSubdirectory
              ? { subdirectory: input.workspaceSubdirectory }
              : {}),
          },
        }
      : {}),
    ...(input.model ? { launch_profile: input.model } : {}),
  };
}

function commandEnvelope(
  operationId: string,
  operation: CommandReceipt["command"],
  sessionId: string | null,
  payload: Record<string, unknown>,
  issuedAt: Date,
  ttlMs = DEFAULT_REMOTE_COMMAND_TTL_MS,
): Record<string, unknown> {
  return {
    request_id: operationId,
    operation,
    target: sessionId
      ? { type: "session", session_id: sessionId }
      : { type: "daemon" },
    issued_at: issuedAt.toISOString(),
    deadline: new Date(issuedAt.getTime() + ttlMs).toISOString(),
    schema_version: 1,
    payload: Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined)),
  };
}

function longApiManagerRequest(path: string): boolean {
  return path.endsWith("/test-model")
    || path.endsWith("/test-endpoint")
    || path.endsWith("/models")
    || path.endsWith("/recover");
}

function extractSessionId(value: unknown): string | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const source = value as Record<string, unknown>;
  for (const key of ["sessionId", "session_id", "id"]) {
    if (typeof source[key] === "string" && source[key]) return source[key] as string;
  }
  for (const key of ["session", "snapshot", "result"]) {
    const nested = extractSessionId(source[key]);
    if (nested) return nested;
  }
  return null;
}

function isUnknownRemoteCompletion(value: unknown): value is OrniTransportError {
  if (!(value instanceof OrniTransportError)
    || !value.retryable
    || !["TIMEOUT", "UNAVAILABLE", "OFFLINE"].includes(value.code)) return false;
  const daemonStatus = value.details?.daemonStatus;
  const receiptOutcome = value.details?.receiptOutcome;
  // An explicit failed/rejected/expired receipt is terminal even when its
  // business error is retryable. Only a transport outage or an explicitly
  // indeterminate Daemon receipt leaves completion unknown.
  if (daemonStatus === "indeterminate" || receiptOutcome === "indeterminate") return true;
  return value.details?.receiptTerminal !== true && typeof daemonStatus !== "string";
}

function unknownCreateCompletion(
  error: OrniTransportError,
  identity: {
    deviceId: string;
    requestedSessionId: string;
    operationId: string;
    commandId?: string;
  },
): OrniTransportError {
  return new OrniTransportError(error.code, error.message, true, {
    ...error.details,
    ...identity,
    phase: "create",
    completionUnknown: true,
  });
}

function unknownStartCompletion(
  error: OrniTransportError,
  identity: {
    deviceId: string;
    requestedSessionId: string;
    operationId: string;
  },
): OrniTransportError {
  return new OrniTransportError(error.code, error.message, true, {
    ...error.details,
    ...identity,
    phase: "start",
    completionUnknown: true,
  });
}

function textOr(value: unknown, fallback: string): string {
  return typeof value === "string" && value ? value : fallback;
}

function nullableString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function integerOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function nullableInteger(value: unknown): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) ? value : null;
}

function commandReceiptStatus(value: unknown): RemoteCommandReceiptStatus {
  const row = record(value);
  return {
    commandId: text(row.commandId),
    deviceId: text(row.deviceId),
    status: text(row.status),
    outcome: nullableText(row.outcome),
    result: row.result === null || row.result === undefined ? null : row.result,
    createdAt: text(row.createdAt),
    updatedAt: text(row.updatedAt),
    receiptId: nullableText(row.receiptId),
    receiptCreatedAt: nullableText(row.receiptCreatedAt),
    error: extractReceiptError(row.error ?? row.result),
  };
}

function isTerminalCommandStatus(status: RemoteCommandReceiptStatus): boolean {
  const outcome = status.outcome ?? status.status;
  return ["applied", "rejected", "failed", "indeterminate", "expired"].includes(outcome);
}

function takeSseBlock(buffer: string): { block: string; rest: string } | null {
  const boundary = /\r?\n\r?\n/.exec(buffer);
  if (!boundary || boundary.index === undefined) return null;
  const next = boundary.index + boundary[0].length;
  return { block: buffer.slice(0, boundary.index), rest: buffer.slice(next) };
}

function terminalCommandReceipt(
  status: RemoteCommandReceiptStatus,
  operation: CommandReceipt["command"],
): CommandReceipt | null {
  const outcome = status.outcome ?? status.status;
  if (outcome !== "applied" && outcome !== "rejected" && outcome !== "failed" && outcome !== "indeterminate" && status.status !== "expired") {
    return null;
  }
  const terminalStatus: CommandReceipt["status"] = outcome === "applied"
    ? "succeeded"
    : outcome === "rejected" || status.status === "expired"
      ? "rejected"
      : "failed";
  return {
    id: status.commandId,
    command: operation,
    status: terminalStatus,
    requestedAt: status.createdAt,
    completedAt: status.receiptCreatedAt ?? status.updatedAt,
    detail: terminalStatus === "succeeded"
      ? "The target computer applied the command."
      : terminalStatus === "rejected"
        ? "The target computer rejected or expired the command."
      : outcome === "indeterminate"
        ? "The target computer returned an indeterminate command result."
        : "The target computer failed to apply the command.",
  };
}

function projectAgentOperationStatus(
  status: RemoteCommandReceiptStatus,
  operation: AgentOperationKind,
): AgentOperationReceipt {
  const command = agentOperationName(operation);
  const terminal = terminalCommandReceipt(status, command);
  const daemonEnvelope = extractDaemonReceiptEnvelope(status.result);
  const error = status.error ?? extractReceiptError(status.result);
  const lifecycleStatus = daemonEnvelope?.status ?? status.outcome ?? status.status;
  if (terminal) return {
    ...terminal,
    detail: error?.message ?? terminal.detail,
    operationId: daemonEnvelope?.requestId ?? status.commandId,
    commandId: status.commandId,
    receiptId: status.receiptId,
    operation,
    targetDeviceId: status.deviceId,
    phase: remoteAgentOperationPhase(lifecycleStatus),
    entityRevision: extractEntityRevision(status.result) ?? extractEntityRevision(status.error),
    retryable: error?.retryable ?? lifecycleStatus === "indeterminate",
    errorCode: error?.code ?? null,
    completionKnown: lifecycleStatus !== "indeterminate",
    observation: "complete",
  };
  const outcome = status.outcome ?? status.status;
  return {
    id: status.commandId,
    command,
    status: outcome === "accepted" ? "accepted" : "running",
    requestedAt: status.createdAt,
    completedAt: null,
    detail: "The target computer accepted the Agent operation and is still processing it.",
    operationId: daemonEnvelope?.requestId ?? status.commandId,
    commandId: status.commandId,
    receiptId: status.receiptId,
    operation,
    targetDeviceId: status.deviceId,
    phase: remoteAgentOperationPhase(lifecycleStatus),
    entityRevision: extractEntityRevision(status.result) ?? extractEntityRevision(status.error),
    retryable: false,
    errorCode: null,
    completionKnown: true,
    observation: "active",
  };
}

function projectEnqueuedAgentOperation(
  response: EnqueuedCommand,
  result: unknown,
  operation: AgentOperationKind,
  fallbackOperationId: string,
  targetDeviceId: string,
): AgentOperationReceipt {
  const envelope = extractDaemonReceiptEnvelope(result);
  const lifecycleStatus = envelope?.status ?? "indeterminate";
  const error = extractReceiptError(result);
  const terminalStatus: AgentOperationReceipt["status"] = lifecycleStatus === "applied"
    ? "succeeded"
    : lifecycleStatus === "rejected" || lifecycleStatus === "expired"
      ? "rejected"
      : "failed";
  return {
    id: response.command.commandId,
    command: agentOperationName(operation),
    status: terminalStatus,
    requestedAt: response.command.createdAt,
    completedAt: response.command.createdAt,
    detail: error?.message ?? (lifecycleStatus === "indeterminate"
      ? "The target computer restarted before the Agent operation outcome was known."
      : "The target computer closed the Agent lifecycle operation."),
    operationId: envelope?.requestId ?? fallbackOperationId,
    commandId: response.command.commandId,
    receiptId: null,
    operation,
    targetDeviceId,
    phase: remoteAgentOperationPhase(lifecycleStatus),
    entityRevision: extractEntityRevision(result),
    retryable: error?.retryable ?? lifecycleStatus === "indeterminate",
    errorCode: error?.code ?? null,
    completionKnown: lifecycleStatus !== "indeterminate",
    observation: "complete",
  };
}

function remoteAgentOperationPhase(status: string): AgentOperationReceipt["phase"] {
  switch (status) {
    case "received":
    case "pending": return "queued";
    case "accepted": return "accepted";
    case "applied": return "completed";
    case "rejected": return "rejected";
    case "indeterminate": return "interrupted";
    case "failed": return "failed";
    case "expired": return "expired";
    case "cancelled": return "cancelled";
    default: return "running";
  }
}

function extractEntityRevision(value: unknown): number | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const revision = row.entity_revision ?? row.entityRevision;
  if (typeof revision === "number" && Number.isSafeInteger(revision) && revision >= 0) {
    return revision;
  }
  return extractEntityRevision(row.result);
}

function isCommandReceiptTerminal(status: CommandReceipt["status"]): boolean {
  return status === "succeeded" || status === "failed" || status === "rejected";
}

function remoteReceiptError(
  status: RemoteCommandReceiptStatus,
  operationLabel: string,
): OrniTransportError {
  const error = status.error ?? extractReceiptError(status.result);
  const daemonEnvelope = extractDaemonReceiptEnvelope(status.result);
  if (!error) {
    return new OrniTransportError(
      "UNAVAILABLE",
      daemonEnvelope?.status === "indeterminate"
        ? `The target computer returned an indeterminate result while trying to ${operationLabel}.`
        : `The target computer could not ${operationLabel}.`,
      daemonEnvelope?.status === "indeterminate",
      {
        commandId: status.commandId,
        deviceId: status.deviceId,
        receiptTerminal: true,
        receiptOutcome: status.outcome ?? status.status,
        ...(daemonEnvelope ? {
          daemonStatus: daemonEnvelope.status,
          ...(daemonEnvelope.commandId ? { daemonCommandId: daemonEnvelope.commandId } : {}),
          ...(daemonEnvelope.requestId ? { daemonRequestId: daemonEnvelope.requestId } : {}),
        } : {}),
      },
    );
  }
  const mapped = error.code.toLowerCase() === "invalid_payload"
    && /schema|version|unsupported command/i.test(error.message)
    ? "UPGRADE_REQUIRED"
    : mapReceiptErrorCode(error.code);
  return new OrniTransportError(
    mapped,
    error.message || `The target computer could not ${operationLabel}.`,
    error.retryable,
    {
      commandId: status.commandId,
      deviceId: status.deviceId,
      receiptTerminal: true,
      receiptOutcome: status.outcome ?? status.status,
      daemonErrorCode: error.code,
      ...(daemonEnvelope ? {
        daemonStatus: daemonEnvelope.status,
        ...(daemonEnvelope.commandId ? { daemonCommandId: daemonEnvelope.commandId } : {}),
        ...(daemonEnvelope.requestId ? { daemonRequestId: daemonEnvelope.requestId } : {}),
      } : {}),
      ...error,
    },
  );
}

function remoteSnapshotHistory(
  snapshot: RemoteTerminalSnapshot,
  sessionId: string,
  query: TerminalHistoryQuery,
): TerminalHistoryResult {
  // Alternate-screen applications (Codex/Claude/OpenCode TUIs) repaint cells
  // in place. The Daemon therefore exposes a bounded display-only projection
  // in `scrollback`; never decode raw PTY redraw frames here because that
  // exposes fragments such as `W`, `Wo`, `Work` and corrupts history.
  if (snapshot.active_screen === "alternate") {
    const lines = snapshot.scrollback_text
      ? snapshot.scrollback_text.split(/\r?\n/u)
      : snapshot.scrollback.map((line) => line.cells.map((cell) => cell.grapheme).join(""));
    const frames = lines.map((text, index) => ({
      id: `terminal:${snapshot.snapshot_id}:alternate:${index}`,
      sequence: Math.max(0, snapshot.watermark - lines.length + index + 1),
      timestamp: new Date(0).toISOString(),
      kind: "snapshot" as const,
      text,
    }));
    return {
      sessionId,
      frames,
      nextCursor: snapshot.watermark,
      generation: snapshot.target.generation,
      truncated: snapshot.truncated,
      hasEarlier: snapshot.truncated && frames.length > 0,
    };
  }
  if (snapshot.history) {
    const frames = snapshot.history.frames.map((frame) => ({
      id: `terminal:${snapshot.snapshot_id}:${frame.sequence}`,
      sequence: frame.sequence,
      timestamp: new Date(0).toISOString(),
      kind: "output" as const,
      text: decodeTerminalHistoryBytes(frame.bytes),
    }));
    return {
      sessionId,
      frames,
      nextCursor: frames.at(-1)?.sequence ?? null,
      generation: snapshot.target.generation,
      truncated: snapshot.history.truncated,
      hasEarlier: snapshot.history.has_earlier,
    };
  }
  const rows = snapshot.scrollback_text
    ? snapshot.scrollback_text.split(/\r?\n/u).map((text) => ({
      cells: Array.from(text, (grapheme) => ({ grapheme })),
    }))
    : [...snapshot.scrollback, ...snapshot.primary];
  const lastSequence = snapshot.watermark;
  const firstSequence = Math.max(0, lastSequence - rows.length + 1);
  const timestamp = new Date(0).toISOString();
  let frames = rows.map((line, index) => ({
    id: `terminal:${snapshot.snapshot_id}:${firstSequence + index}`,
    sequence: firstSequence + index,
    timestamp,
    kind: "snapshot" as const,
    text: line.cells.map((cell) => cell.grapheme).join(""),
  }));
  if (query.afterSequence !== undefined) {
    frames = frames.filter((frame) => frame.sequence > query.afterSequence!);
  }
  if (query.beforeSequence !== undefined) {
    frames = frames.filter((frame) => frame.sequence < query.beforeSequence!);
  }
  const limit = Math.min(1_000, Math.max(1, query.limit ?? (frames.length || 1)));
  if (query.beforeSequence !== undefined || query.afterSequence === undefined) {
    frames = frames.slice(-limit);
  } else {
    frames = frames.slice(0, limit);
  }
  return {
    sessionId,
    frames,
    nextCursor: frames.at(-1)?.sequence ?? null,
    generation: snapshot.target.generation,
    truncated: snapshot.truncated,
    hasEarlier: snapshot.truncated,
  };
}

function decodeTerminalHistoryBytes(bytes: number[]): string {
  // History is a display projection, not a second VT bootstrap. Strip control
  // sequences before exposing it to the text-oriented history API so callers
  // can safely render and search it without redrawing the live TUI.
  return new TextDecoder()
    .decode(Uint8Array.from(bytes))
    .replace(/\u001b(?:\[[0-?]*[ -/]*[@-~]|\][^\u0007]*(?:\u0007|\u001b\\))/g, "")
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, "");
}

function extractReceiptError(value: unknown): RemoteReceiptError | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const candidate = record.error && typeof record.error === "object" && !Array.isArray(record.error)
    ? record.error as Record<string, unknown>
    : record;
  if (typeof candidate.code === "string" && typeof candidate.message === "string") {
    return {
      code: candidate.code,
      message: candidate.message,
      retryable: candidate.retryable === true,
      ...candidate,
    };
  }
  if (candidate !== record) {
    const nested = extractReceiptError(candidate);
    if (nested) return nested;
  }
  return extractReceiptError(record.result);
}

function extractDaemonReceiptEnvelope(value: unknown): {
  status: string;
  commandId: string | null;
  requestId: string | null;
} | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const nested = extractDaemonReceiptEnvelope(row.result);
  if (nested) return nested;
  if (typeof row.status !== "string") return null;
  const commandId = row.commandId ?? row.command_id;
  const requestId = row.requestId ?? row.request_id;
  if (typeof commandId !== "string" && typeof requestId !== "string") return null;
  return {
    status: row.status,
    commandId: typeof commandId === "string" ? commandId : null,
    requestId: typeof requestId === "string" ? requestId : null,
  };
}

function mapReceiptErrorCode(code: string): import("./transport.ts").TransportErrorCode {
  switch (code.toLowerCase()) {
    case "unauthenticated": return "UNAUTHENTICATED";
    case "forbidden": return "PERMISSION_DENIED";
    case "unsupported": return "UNSUPPORTED";
    case "schema_mismatch":
    case "version_mismatch":
    case "upgrade_required": return "UPGRADE_REQUIRED";
    case "invalid_payload": return "CONTRACT_INVALID";
    case "invalid_transition":
    case "revision_conflict":
    case "request_conflict": return "CONFLICT";
    case "deadline_exceeded": return "TIMEOUT";
    case "runtime_unavailable":
    case "provider_unavailable": return "UNAVAILABLE";
    case "not_found": return "NOT_FOUND";
    default: return "UNAVAILABLE";
  }
}

function resolvedSourcesKey(sources: readonly ProviderHistorySourceInput[]): string {
  if (!sources.length) return "";
  return JSON.stringify([...sources]
    .map((source) => ({ provider: source.provider, homeDirectory: source.homeDirectory }))
    .sort((left, right) => `${left.provider}:${left.homeDirectory}`.localeCompare(`${right.provider}:${right.homeDirectory}`)));
}

function providerHistoryCacheKey(deviceId: string, provider: ProviderId | undefined, sourcesKey: string): string {
  return `${deviceId}|${provider ?? "*"}|${sourcesKey}`;
}

function providerHistoryHomeKey(deviceId: string, provider: string): string {
  return `${deviceId}|${provider}`;
}

function isRetryableTerminalAttachError(value: unknown): boolean {
  if (!(value instanceof OrniTransportError) || !value.retryable) return false;
  // A relay handshake can fail while the Daemon is publishing a replacement
  // process attempt. Keep auth/policy/session-inactive errors terminal, but
  // allow a short fresh-ticket retry for transport and target races.
  return !/(?:ticket|token)\s*(?:expired|invalid)|policy|unauthori[sz]ed|permission|forbidden|session (?:stopped|closed|not found)/i.test(value.message);
}

function assertRemoteTerminalProjectionActive(projection: CloudProjection): void {
  if (["running", "waiting"].includes(projection.status)) return;
  throw new OrniTransportError(
    "CONFLICT",
    "The Session is not active; start it before connecting the terminal.",
  );
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

function remainingDeadlineMs(deadlineAt: number | null): number {
  return deadlineAt === null ? Number.MAX_SAFE_INTEGER : Math.max(0, deadlineAt - Date.now());
}

/** Apply one caller-owned acquisition deadline without leaking a timer. */
function withDeadline<T>(task: Promise<T>, deadlineAt: number | null, message: string): Promise<T> {
  if (deadlineAt === null) return task;
  const remaining = remainingDeadlineMs(deadlineAt);
  if (remaining <= 0) {
    void task.catch(() => undefined);
    return Promise.reject(new OrniTransportError("TIMEOUT", message, true));
  }
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new OrniTransportError("TIMEOUT", message, true));
      // The underlying Fetch/WebSocket operation cannot be cancelled through
      // the transport interface. Observe its eventual rejection so the
      // timeout does not create an unhandled promise.
      void task.catch(() => undefined);
    }, remaining);
    task.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (cause) => { clearTimeout(timer); reject(cause); },
    );
  });
}

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw contractError("Expected an object.");
  return value as Record<string, unknown>;
}
function array(value: unknown): unknown[] {
  if (!Array.isArray(value)) throw contractError("Expected an array.");
  return value;
}
function text(value: unknown): string { if (typeof value !== "string") throw contractError("Expected text."); return value; }
function nullableText(value: unknown): string | null { return value === null ? null : text(value); }
function nullableTextOr(value: unknown): string | null { return typeof value === "string" ? value : null; }
function integer(value: unknown): number { if (!Number.isSafeInteger(value)) throw contractError("Expected an integer."); return value as number; }
function stringArray(value: unknown): string[] { if (!Array.isArray(value) || value.some((item) => typeof item !== "string")) throw contractError("Expected text array."); return value as string[]; }
function stringArrayOr(value: unknown): string[] { return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : []; }
function isProviderSelectorCompatibilityError(value: unknown): boolean {
  if (!(value instanceof Error)) return false;
  const message = value.message.toLowerCase();
  return ["CONTRACT_INVALID", "INVALID_ARGUMENT"].includes((value as OrniTransportError).code)
    || message.includes("unknown field provider")
    || message.includes("expected sources")
    || message.includes("unsupported history provider");
}

function isHistoryHomeDirectoryCompatibilityError(value: unknown): boolean {
  if (!(value instanceof OrniTransportError) || value.retryable) return false;
  if (value.code !== "CONTRACT_INVALID" && value.code !== "INVALID_ARGUMENT") return false;
  const message = value.message.toLowerCase();
  return message.includes("unknown field")
    && /history[._ ]home[._ ]directory/.test(message);
}

function isManagementMode(value: string): value is SessionManagementMode { return value === "native"; }
function isStatus(value: string): value is SessionStatus { return ["starting", "running", "waiting", "stopping", "stopped", "exited", "error", "archived"].includes(value); }
function isAttention(value: string): value is SessionAttention { return ["none", "info", "action", "critical"].includes(value); }
function isVisibility(value: string): value is SessionVisibility { return ["attached", "background", "hidden"].includes(value); }
function contractError(message: string): OrniTransportError { return new OrniTransportError("CONTRACT_INVALID", message); }
function requiredRandomUuid(): string {
  return randomUuid();
}
